import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "@/App";
import { canEditModule } from "@/utils/permissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Contact2, Plus, Search, Filter, Building2, User, X,
  Trash2, Edit2, Loader2, ImageIcon, Star, Phone, Mail, MessageCircle,
  Globe, MapPin, Upload, CheckCircle2, AlertTriangle, StickyNote, ClipboardList,
  Send, Settings as SettingsIcon, RefreshCw, Zap, Eye,
  Download, FileSpreadsheet, FileText, LayoutDashboard, CalendarRange, TrendingUp,
  Ban, Copy, XCircle, Monitor, Smartphone,
} from "lucide-react";

const MODULE_KEY = "clientes_directorio_contactos";

// Ladas mundiales frecuentes (México primero por defecto).
const LADAS_MUNDIALES = [
  { code: "+52", label: "🇲🇽 +52 México" },
  { code: "+1", label: "🇺🇸 +1 EE.UU./Canadá" },
  { code: "+34", label: "🇪🇸 +34 España" },
  { code: "+55", label: "🇧🇷 +55 Brasil" },
  { code: "+54", label: "🇦🇷 +54 Argentina" },
  { code: "+56", label: "🇨🇱 +56 Chile" },
  { code: "+57", label: "🇨🇴 +57 Colombia" },
  { code: "+51", label: "🇵🇪 +51 Perú" },
  { code: "+58", label: "🇻🇪 +58 Venezuela" },
  { code: "+593", label: "🇪🇨 +593 Ecuador" },
  { code: "+506", label: "🇨🇷 +506 Costa Rica" },
  { code: "+507", label: "🇵🇦 +507 Panamá" },
  { code: "+502", label: "🇬🇹 +502 Guatemala" },
  { code: "+53", label: "🇨🇺 +53 Cuba" },
  { code: "+1809", label: "🇩🇴 +1809 Rep. Dominicana" },
  { code: "+33", label: "🇫🇷 +33 Francia" },
  { code: "+44", label: "🇬🇧 +44 Reino Unido" },
  { code: "+39", label: "🇮🇹 +39 Italia" },
  { code: "+49", label: "🇩🇪 +49 Alemania" },
  { code: "+81", label: "🇯🇵 +81 Japón" },
  { code: "+86", label: "🇨🇳 +86 China" },
];
const LADA_CODES = LADAS_MUNDIALES.map((l) => l.code).sort((a, b) => b.length - a.length);

/** Devuelve el teléfono con lada garantizada. Si no viene con "+", prefija "+52". */
function formatPhone(v) {
  if (!v) return "";
  const s = String(v).trim();
  if (s.startsWith("+")) return s;
  return `+52 ${s}`.trim();
}

/** Parte un valor combinado "+52 5544332211" en {lada, numero}. Si no viene con "+",
 *  asume que es local mexicano y devuelve lada default vacía + número completo. */
function parsePhone(v) {
  if (!v) return { lada: "+52", numero: "" };
  const s = String(v).trim();
  if (s.startsWith("+")) {
    // Detectar la lada más larga que haga match para no confundir +1 con +1809
    for (const code of LADA_CODES) {
      if (s.startsWith(code)) {
        return { lada: code, numero: s.slice(code.length).trim() };
      }
    }
    // "+" pero no reconocida: separar por primer espacio
    const idx = s.indexOf(" ");
    if (idx > 0) return { lada: s.slice(0, idx), numero: s.slice(idx + 1).trim() };
    return { lada: s, numero: "" };
  }
  // Sin +: usar default MX
  return { lada: "+52", numero: s };
}

/** Componente reutilizable: input de teléfono con selector de lada. */
function PhoneField({ label, value, onChange, placeholder = "10 dígitos", testid = "phone" }) {
  const parsed = parsePhone(value);
  const setLada = (l) => onChange(`${l} ${parsed.numero}`.trim());
  const setNumero = (n) => {
    // Solo dígitos y espacios
    const clean = n.replace(/[^\d\s]/g, "");
    onChange(clean ? `${parsed.lada} ${clean}`.trim() : "");
  };
  return (
    <div>
      {label && <Label>{label}</Label>}
      <div className="flex gap-1.5">
        <Select value={parsed.lada} onValueChange={setLada}>
          <SelectTrigger className="w-32" data-testid={`${testid}-lada`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="max-h-64">
            {LADAS_MUNDIALES.map((l) => (
              <SelectItem key={l.code} value={l.code}>{l.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          value={parsed.numero}
          onChange={(e) => setNumero(e.target.value)}
          placeholder={placeholder}
          inputMode="tel"
          className="flex-1"
          data-testid={`${testid}-numero`}
        />
      </div>
    </div>
  );
}


// ---- Helpers ----
const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
};

const estatusColor = {
  "Activo": "bg-emerald-100 text-emerald-700 border-emerald-200",
  "Por contactar": "bg-amber-100 text-amber-700 border-amber-200",
  "En seguimiento": "bg-blue-100 text-blue-700 border-blue-200",
  "Inactivo": "bg-slate-200 text-slate-600 border-slate-300",
  "Datos incompletos": "bg-red-100 text-red-700 border-red-200",
  // Estatus de campañas
  "borrador": "bg-slate-100 text-slate-700 border-slate-200",
  "programada": "bg-amber-100 text-amber-700 border-amber-200",
  "enviada": "bg-emerald-100 text-emerald-700 border-emerald-200",
  "cancelada": "bg-slate-200 text-slate-500 border-slate-300",
  "error": "bg-red-100 text-red-700 border-red-200",
};

// Multi-select simple con checkboxes en Popover (usa Select nativo minimalista)
const MultiSelect = ({ label, options, value, onChange, testid }) => {
  const [open, setOpen] = useState(false);
  const safeValue = Array.isArray(value) ? value : [];
  const toggle = (opt) => {
    const next = safeValue.includes(opt) ? safeValue.filter((v) => v !== opt) : [...safeValue, opt];
    onChange(next);
  };
  return (
    <div className="space-y-1">
      <Label className="text-xs text-slate-600">{label}</Label>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full text-left px-3 py-1.5 text-sm border border-slate-200 rounded-md bg-white hover:border-slate-300 flex items-center justify-between"
        data-testid={`ms-${testid}-toggle`}
      >
        <span className="truncate">
          {safeValue.length === 0 ? <span className="text-slate-400">Todos</span> : `${safeValue.length} seleccionado(s)`}
        </span>
        <Filter className="w-3.5 h-3.5 text-slate-400 shrink-0" />
      </button>
      {open && (
        <div className="border border-slate-200 rounded-md bg-white shadow-md p-2 max-h-52 overflow-y-auto space-y-0.5">
          {(options || []).map((opt) => (
            <label key={opt} className="flex items-center gap-2 px-2 py-1 text-sm hover:bg-slate-50 rounded cursor-pointer">
              <input type="checkbox" checked={safeValue.includes(opt)} onChange={() => toggle(opt)} data-testid={`ms-${testid}-${opt}`} />
              <span>{opt}</span>
            </label>
          ))}
          {safeValue.length > 0 && (
            <button className="w-full text-xs text-blue-600 mt-1 hover:underline" onClick={() => onChange([])}>Limpiar</button>
          )}
        </div>
      )}
    </div>
  );
};

// ---- Página principal ----
export default function DirectorioContactos() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || canEditModule(user, MODULE_KEY);

  const [catalogo, setCatalogo] = useState(null);
  const [tab, setTab] = useState("general");

  // Empresas state
  const [empresas, setEmpresas] = useState([]);
  const [empresasLoading, setEmpresasLoading] = useState(false);
  const [empFilters, setEmpFilters] = useState({ q: "", tipos: [], categorias: [], especialidades: [], destinos: [], niveles: [], estatus: [] });
  const [empresaOpen, setEmpresaOpen] = useState(null); // ficha empresa
  const [empresaForm, setEmpresaForm] = useState(null); // dialog nueva/editar

  // Contactos state
  const [contactos, setContactos] = useState([]);
  const [contactosLoading, setContactosLoading] = useState(false);
  const [cFilters, setCFilters] = useState({ q: "", tipos: [], estatus: [] });
  const [contactoOpen, setContactoOpen] = useState(null);
  const [contactoForm, setContactoForm] = useState(null);

  // Historial state
  const [interacciones, setInteracciones] = useState([]);
  const [interLoading, setInterLoading] = useState(false);
  const [interForm, setInterForm] = useState(null);
  const [hFilters, setHFilters] = useState({ empresa_id: "", contacto_id: "", tipo: "", registrado_por: "", fecha_desde: "", fecha_hasta: "" });

  // Campañas state (para filtros en la pestaña Campañas)
  const [campFilters, setCampFilters] = useState({ q: "", estados: [], fecha_desde: "", fecha_hasta: "" });

  // General
  const [general, setGeneral] = useState(null);
  const [generalLoading, setGeneralLoading] = useState(false);

  // Reporte
  const [reportOpen, setReportOpen] = useState(null); // {tipo, filtros}

  // Autorizaciones (supervisor/admin)
  const [usuarios, setUsuarios] = useState([]);
  const isSupervisorOrAdmin = user?.role === "admin" || user?.role === "supervisor";

  // Cargar lista de usuarios para dropdowns "Responsable PD"
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/users-list`, { withCredentials: true });
        setUsuarios(r.data || []);
      } catch { /* silent */ }
    })();
  }, []);

  // Índice user_id → nombre para resolver "Responsable PD" almacenado como id
  const usuariosIndex = useMemo(() => {
    const m = new Map();
    usuarios.forEach((u) => m.set(u.id, u.name || u.email || u.id));
    return m;
  }, [usuarios]);
  const resolverResponsable = useCallback((v) => {
    if (!v) return "—";
    return usuariosIndex.get(v) || v; // si no está en el mapa (registro viejo con nombre plano), muestra tal cual
  }, [usuariosIndex]);

  // Cargar catálogo
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/directorio/catalogo`, { withCredentials: true });
        setCatalogo(r.data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "No se pudo cargar el catálogo");
      }
    })();
  }, []);

  // Cargar empresas
  const loadEmpresas = useCallback(async () => {
    setEmpresasLoading(true);
    try {
      const params = new URLSearchParams();
      if (empFilters.q) params.set("q", empFilters.q);
      ["tipos", "categorias", "especialidades", "destinos", "niveles", "estatus"].forEach((k) => {
        (empFilters[k] || []).forEach((v) => params.append(k, v));
      });
      params.set("page_size", "200");
      const r = await axios.get(`${API}/directorio/empresas?${params.toString()}`, { withCredentials: true });
      setEmpresas(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando empresas");
    } finally {
      setEmpresasLoading(false);
    }
  }, [empFilters]);

  // Cargar contactos
  const loadContactos = useCallback(async () => {
    setContactosLoading(true);
    try {
      const params = new URLSearchParams();
      if (cFilters.q) params.set("q", cFilters.q);
      ["tipos", "estatus"].forEach((k) => (cFilters[k] || []).forEach((v) => params.append(k, v)));
      params.set("page_size", "500");
      const r = await axios.get(`${API}/directorio/contactos?${params.toString()}`, { withCredentials: true });
      setContactos(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando contactos");
    } finally {
      setContactosLoading(false);
    }
  }, [cFilters]);

  // Cargar historial (con filtros)
  const loadInter = useCallback(async () => {
    setInterLoading(true);
    try {
      const params = new URLSearchParams();
      if (hFilters.empresa_id) params.set("empresa_id", hFilters.empresa_id);
      if (hFilters.contacto_id) params.set("contacto_id", hFilters.contacto_id);
      if (hFilters.tipo) params.set("tipo", hFilters.tipo);
      if (hFilters.fecha_desde) params.set("fecha_desde", hFilters.fecha_desde);
      if (hFilters.fecha_hasta) params.set("fecha_hasta", hFilters.fecha_hasta);
      const r = await axios.get(`${API}/directorio/interacciones?${params.toString()}`, { withCredentials: true });
      let rows = r.data || [];
      // Filtro cliente-side por registrado_por (backend no lo soporta como query)
      if (hFilters.registrado_por) {
        const q = hFilters.registrado_por.toLowerCase();
        rows = rows.filter((r) => (r.registrado_por || "").toLowerCase().includes(q));
      }
      setInteracciones(rows);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando historial");
    } finally {
      setInterLoading(false);
    }
  }, [hFilters]);

  // Cargar vista general
  const loadGeneral = useCallback(async () => {
    setGeneralLoading(true);
    try {
      const r = await axios.get(`${API}/directorio/general/resumen`, { withCredentials: true });
      setGeneral(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando vista general");
    } finally {
      setGeneralLoading(false);
    }
  }, []);

  useEffect(() => { if (tab === "empresas") loadEmpresas(); }, [tab, loadEmpresas]);
  useEffect(() => { if (tab === "contactos") loadContactos(); }, [tab, loadContactos]);
  useEffect(() => { if (tab === "historial") loadInter(); }, [tab, loadInter]);
  useEffect(() => { if (tab === "general") loadGeneral(); }, [tab, loadGeneral]);

  // Mapa rápido empresa_id → nombre para la vista Contactos e Historial
  const empresasIndex = useMemo(() => {
    const m = new Map();
    empresas.forEach((e) => m.set(e.id, e));
    return m;
  }, [empresas]);
  // Si no tenemos empresas cargadas, las traemos siempre (útil para el diálogo de interacción y para el índice)
  useEffect(() => {
    if (empresas.length === 0) {
      (async () => {
        try {
          const r = await axios.get(`${API}/directorio/empresas?page_size=500`, { withCredentials: true });
          setEmpresas(r.data.items || []);
        } catch { /* silent */ }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!catalogo) {
    return (
      <div className="flex items-center gap-2 p-8 text-slate-600">
        <Loader2 className="w-5 h-5 animate-spin" /> Cargando catálogos…
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1400px] mx-auto space-y-5" data-testid="directorio-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl border border-[#0037ff]/15 bg-[#0037ff]/5 flex items-center justify-center">
            <Contact2 className="w-5 h-5 text-[#0037ff]" strokeWidth={1.75} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "'Mulish', sans-serif" }}>Directorio de Contactos</h1>
            <p className="text-sm text-slate-500">Hoteles, DMCs, productoras, clientes y aliados de PD Travel</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!canEdit && (
            <Badge variant="outline" className="text-slate-500">Solo lectura</Badge>
          )}
          <Button
            onClick={() => setInterForm({})}
            className="bg-[#0037ff] hover:bg-[#002bcc]"
            data-testid="btn-header-registrar-interaccion"
          >
            <ClipboardList className="w-4 h-4 mr-1.5" /> Registrar interacción
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100 p-1">
          <TabsTrigger value="general" data-testid="tab-general">
            <LayoutDashboard className="w-4 h-4 mr-1.5" /> General
          </TabsTrigger>
          <TabsTrigger value="empresas" data-testid="tab-empresas">
            <Building2 className="w-4 h-4 mr-1.5" /> Empresas
          </TabsTrigger>
          <TabsTrigger value="contactos" data-testid="tab-contactos">
            <User className="w-4 h-4 mr-1.5" /> Contactos
          </TabsTrigger>
          <TabsTrigger value="historial" data-testid="tab-historial">
            <ClipboardList className="w-4 h-4 mr-1.5" /> Historial
          </TabsTrigger>
          <TabsTrigger value="campanas" data-testid="tab-campanas">
            <Send className="w-4 h-4 mr-1.5" /> Campañas
          </TabsTrigger>
          {isSupervisorOrAdmin && (
            <TabsTrigger value="autorizaciones" data-testid="tab-autorizaciones">
              <AlertTriangle className="w-4 h-4 mr-1.5" /> Autorizaciones
            </TabsTrigger>
          )}
          <TabsTrigger value="config" data-testid="tab-config">
            <SettingsIcon className="w-4 h-4 mr-1.5" /> Configuración
          </TabsTrigger>
        </TabsList>

        {/* ─── GENERAL ─── */}
        <TabsContent value="general" className="space-y-4 mt-4">
          <GeneralPanel
            data={general}
            loading={generalLoading}
            onOpenEmpresa={(id) => setEmpresaOpen(id)}
            onDownload={() => setReportOpen({ tipo: "general", filtros: {} })}
          />
        </TabsContent>

        {/* ─── EMPRESAS ─── */}
        <TabsContent value="empresas" className="space-y-3 mt-4">
          <Card>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    className="pl-9"
                    placeholder="Buscar por nombre, país, ciudad, web, responsable…"
                    value={empFilters.q}
                    onChange={(e) => setEmpFilters({ ...empFilters, q: e.target.value })}
                    data-testid="empresas-search"
                  />
                </div>
                {canEdit && (
                  <Button onClick={() => setEmpresaForm({})} className="bg-[#0037ff] hover:bg-[#002bcc]" data-testid="btn-nueva-empresa">
                    <Plus className="w-4 h-4 mr-1" /> Nueva empresa
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => setReportOpen({ tipo: "empresas", filtros: empFilters })}
                  data-testid="btn-descargar-empresas"
                  className="border-[#0037ff]/30 text-[#0037ff] hover:bg-[#0037ff]/5"
                >
                  <Download className="w-4 h-4 mr-1.5" /> Descargar reporte
                </Button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                <MultiSelect label="Tipo de relación" options={catalogo.tipo_relacion} value={empFilters.tipos} onChange={(v) => setEmpFilters({ ...empFilters, tipos: v })} testid="tipos" />
                <MultiSelect label="Categoría" options={catalogo.categoria} value={empFilters.categorias} onChange={(v) => setEmpFilters({ ...empFilters, categorias: v })} testid="categorias" />
                <MultiSelect label="Especialidad" options={catalogo.especialidad} value={empFilters.especialidades} onChange={(v) => setEmpFilters({ ...empFilters, especialidades: v })} testid="especialidades" />
                <MultiSelect label="Nivel de relación" options={catalogo.nivel_relacion} value={empFilters.niveles} onChange={(v) => setEmpFilters({ ...empFilters, niveles: v })} testid="niveles" />
                <MultiSelect label="Estatus" options={catalogo.estatus_empresa} value={empFilters.estatus} onChange={(v) => setEmpFilters({ ...empFilters, estatus: v })} testid="estatus-emp" />
                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">Ciudad / destino</Label>
                  <Input placeholder="Ej. Punta Cana" value={(empFilters.destinos || []).join(",")}
                    onChange={(e) => setEmpFilters({ ...empFilters, destinos: e.target.value ? e.target.value.split(",").map(s => s.trim()).filter(Boolean) : [] })}
                    data-testid="destinos-input" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-base font-semibold text-slate-700">
                {empresasLoading ? "Cargando…" : `${empresas.length} empresa(s)`}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="[&>th]:py-2 [&>th]:text-xs [&>th]:font-semibold">
                    <TableHead>Logo</TableHead>
                    <TableHead>Empresa</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Categoría</TableHead>
                    <TableHead>Especialidades</TableHead>
                    <TableHead>Destino</TableHead>
                    <TableHead>Estatus</TableHead>
                    <TableHead>Responsable</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {empresas.length === 0 && !empresasLoading && (
                    <TableRow><TableCell colSpan={8} className="text-center text-slate-400 py-8">Sin resultados</TableCell></TableRow>
                  )}
                  {empresas.map((e) => (
                    <TableRow
                      key={e.id}
                      className="cursor-pointer hover:bg-slate-50 [&>td]:py-2 [&>td]:whitespace-nowrap"
                      onClick={() => setEmpresaOpen(e.id)}
                      data-testid={`empresa-row-${e.id}`}
                    >
                      <TableCell>
                        {e.logo_url ? (
                          <div className="w-10 h-10 bg-slate-50 rounded flex items-center justify-center overflow-hidden border">
                            <img src={e.logo_url} alt={e.nombre} className="max-w-full max-h-full object-contain" />
                          </div>
                        ) : (
                          <div className="w-10 h-10 bg-slate-100 rounded flex items-center justify-center text-slate-400">
                            <Building2 className="w-5 h-5" />
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="font-medium text-sm max-w-[240px] truncate" title={e.nombre}>{e.nombre}</TableCell>
                      <TableCell className="text-sm">{e.tipo_relacion || "—"}</TableCell>
                      <TableCell className="text-sm">{e.categoria || "—"}</TableCell>
                      <TableCell className="text-xs max-w-[220px] truncate" title={(e.especialidades || []).join(", ")}>
                        {(e.especialidades || []).slice(0, 2).map((s) => <Badge key={s} variant="outline" className="mr-1 text-[10px]">{s}</Badge>)}
                        {(e.especialidades || []).length > 2 && <span className="text-slate-400 text-[10px]">+{e.especialidades.length - 2}</span>}
                      </TableCell>
                      <TableCell className="text-sm">{[e.ciudad, e.pais].filter(Boolean).join(", ") || "—"}</TableCell>
                      <TableCell><Badge className={`${estatusColor[e.estatus] || "bg-slate-100 text-slate-600"} text-[10px] px-1.5 py-0`}>{e.estatus || "—"}</Badge></TableCell>
                      <TableCell className="text-sm">{resolverResponsable(e.responsable_pd)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── CONTACTOS ─── */}
        <TabsContent value="contactos" className="space-y-3 mt-4">
          <Card>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input className="pl-9" placeholder="Buscar por nombre, correo, puesto, área…"
                    value={cFilters.q} onChange={(e) => setCFilters({ ...cFilters, q: e.target.value })} data-testid="contactos-search" />
                </div>
                {canEdit && (
                  <Button onClick={() => setContactoForm({})} className="bg-[#0037ff] hover:bg-[#002bcc]" data-testid="btn-nuevo-contacto">
                    <Plus className="w-4 h-4 mr-1" /> Nuevo contacto
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => setReportOpen({ tipo: "contactos", filtros: cFilters })}
                  data-testid="btn-descargar-contactos"
                  className="border-[#0037ff]/30 text-[#0037ff] hover:bg-[#0037ff]/5"
                >
                  <Download className="w-4 h-4 mr-1.5" /> Descargar reporte
                </Button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <MultiSelect label="Tipo de relación" options={catalogo.tipo_relacion} value={cFilters.tipos} onChange={(v) => setCFilters({ ...cFilters, tipos: v })} testid="c-tipos" />
                <MultiSelect label="Estatus" options={catalogo.estatus_contacto} value={cFilters.estatus} onChange={(v) => setCFilters({ ...cFilters, estatus: v })} testid="c-estatus" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-base font-semibold text-slate-700">
                {contactosLoading ? "Cargando…" : `${contactos.length} contacto(s)`}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="[&>th]:py-2 [&>th]:text-xs [&>th]:font-semibold">
                    <TableHead>Foto</TableHead>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Puesto</TableHead>
                    <TableHead>Empresa</TableHead>
                    <TableHead>Correo</TableHead>
                    <TableHead>Teléfono</TableHead>
                    <TableHead>Estatus</TableHead>
                    <TableHead>MC</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contactos.length === 0 && !contactosLoading && (
                    <TableRow><TableCell colSpan={8} className="text-center text-slate-400 py-8">Sin resultados</TableCell></TableRow>
                  )}
                  {contactos.map((c) => {
                    const emp = empresasIndex.get(c.empresa_id);
                    return (
                      <TableRow key={c.id} onClick={() => setContactoOpen(c.id)}
                        className="cursor-pointer hover:bg-slate-50 [&>td]:py-2 [&>td]:whitespace-nowrap" data-testid={`contacto-row-${c.id}`}>
                        <TableCell>
                          {c.foto_url ? (
                            <div className="w-9 h-9 rounded-full overflow-hidden border"><img src={c.foto_url} alt="" className="w-full h-full object-cover" /></div>
                          ) : (
                            <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-400"><User className="w-4 h-4" /></div>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">
                          <div className="flex items-center gap-1">
                            {c.es_principal && <Star className="w-3 h-3 text-amber-500 fill-amber-500" />}
                            <span className="font-medium">{[c.nombre, c.apellidos].filter(Boolean).join(" ")}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">{c.puesto || "—"}</TableCell>
                        <TableCell className="text-sm max-w-[180px] truncate">{emp?.nombre || "—"}</TableCell>
                        <TableCell className="text-sm">{c.correo || "—"}</TableCell>
                        <TableCell className="text-sm">{formatPhone(c.telefono) || formatPhone(c.whatsapp) || "—"}</TableCell>
                        <TableCell><Badge className={`${estatusColor[c.estatus] || ""} text-[10px] px-1.5 py-0`}>{c.estatus}</Badge></TableCell>
                        <TableCell className="text-xs text-slate-500">{c.mailchimp_status || "—"}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── HISTORIAL ─── */}
        <TabsContent value="historial" className="space-y-3 mt-4">
          <Card>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm text-slate-600">Bitácora de todas las interacciones registradas por el equipo</p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setReportOpen({ tipo: "historial", filtros: hFilters })}
                    data-testid="btn-descargar-historial"
                    className="border-[#0037ff]/30 text-[#0037ff] hover:bg-[#0037ff]/5"
                  >
                    <Download className="w-4 h-4 mr-1.5" /> Descargar reporte
                  </Button>
                  <Button onClick={() => setInterForm({})} className="bg-[#0037ff] hover:bg-[#002bcc]" data-testid="btn-nueva-interaccion">
                    <Plus className="w-4 h-4 mr-1" /> Registrar interacción
                  </Button>
                </div>
              </div>
              {/* Filtros */}
              <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">Empresa</Label>
                  <Select value={hFilters.empresa_id || "__all__"} onValueChange={(v) => setHFilters({ ...hFilters, empresa_id: v === "__all__" ? "" : v, contacto_id: "" })}>
                    <SelectTrigger data-testid="h-filter-empresa"><SelectValue placeholder="Todas" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">Todas</SelectItem>
                      {empresas.map((e) => <SelectItem key={e.id} value={e.id}>{e.nombre}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">Tipo</Label>
                  <Select value={hFilters.tipo || "__all__"} onValueChange={(v) => setHFilters({ ...hFilters, tipo: v === "__all__" ? "" : v })}>
                    <SelectTrigger data-testid="h-filter-tipo"><SelectValue placeholder="Todos" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">Todos</SelectItem>
                      {(catalogo.tipo_interaccion || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">Registrado por</Label>
                  <Input placeholder="Nombre" value={hFilters.registrado_por} onChange={(e) => setHFilters({ ...hFilters, registrado_por: e.target.value })} data-testid="h-filter-registrado" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">Desde</Label>
                  <Input type="date" value={hFilters.fecha_desde} onChange={(e) => setHFilters({ ...hFilters, fecha_desde: e.target.value })} data-testid="h-filter-desde" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">Hasta</Label>
                  <Input type="date" value={hFilters.fecha_hasta} onChange={(e) => setHFilters({ ...hFilters, fecha_hasta: e.target.value })} data-testid="h-filter-hasta" />
                </div>
                <div className="flex items-end">
                  <Button
                    variant="ghost"
                    onClick={() => setHFilters({ empresa_id: "", contacto_id: "", tipo: "", registrado_por: "", fecha_desde: "", fecha_hasta: "" })}
                    className="text-xs text-slate-500 hover:text-slate-700"
                    data-testid="h-filter-clear"
                  >
                    <X className="w-3.5 h-3.5 mr-1" /> Limpiar filtros
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="[&>th]:py-2 [&>th]:text-xs [&>th]:font-semibold">
                    <TableHead>Fecha</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Empresa</TableHead>
                    <TableHead>Proyecto</TableHead>
                    <TableHead>Registrado por</TableHead>
                    <TableHead>Nota</TableHead>
                    <TableHead>Próximo paso</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {interacciones.length === 0 && !interLoading && (
                    <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">Sin interacciones registradas</TableCell></TableRow>
                  )}
                  {interacciones.map((i) => (
                    <TableRow key={i.id} className="[&>td]:py-1.5">
                      <TableCell className="text-xs whitespace-nowrap">{i.fecha}</TableCell>
                      <TableCell className="text-sm whitespace-nowrap">{i.tipo}</TableCell>
                      <TableCell className="text-sm max-w-[180px] truncate">{empresasIndex.get(i.empresa_id)?.nombre || "—"}</TableCell>
                      <TableCell className="text-sm max-w-[180px] truncate">{i.proyecto || "—"}</TableCell>
                      <TableCell className="text-sm whitespace-nowrap">{i.registrado_por}</TableCell>
                      <TableCell className="text-sm max-w-[300px] truncate" title={i.nota}>{i.nota}</TableCell>
                      <TableCell className="text-sm max-w-[220px] truncate" title={i.proximo_paso}>{i.proximo_paso || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── CAMPAÑAS ─── */}
        <TabsContent value="campanas" className="mt-4">
          <CampanasPanel
            canEdit={canEdit}
            catalogo={catalogo}
            empresas={empresas}
            filters={campFilters}
            setFilters={setCampFilters}
            onDownload={() => setReportOpen({ tipo: "campanas", filtros: campFilters })}
          />
        </TabsContent>

        {/* ─── AUTORIZACIONES (supervisor/admin) ─── */}
        {isSupervisorOrAdmin && (
          <TabsContent value="autorizaciones" className="mt-4">
            <AutorizacionesPanel active={tab === "autorizaciones"} />
          </TabsContent>
        )}

        {/* ─── CONFIGURACIÓN ─── */}
        <TabsContent value="config" className="mt-4">
          <ConfigPanel canEdit={canEdit} />
        </TabsContent>
      </Tabs>

      {/* ─── Dialogs ─── */}
      {empresaOpen && (
        <FichaEmpresa
          empresaId={empresaOpen}
          canEdit={canEdit}
          catalogo={catalogo}
          usuariosIndex={usuariosIndex}
          onClose={() => setEmpresaOpen(null)}
          onEdit={(emp) => { setEmpresaOpen(null); setEmpresaForm(emp); }}
          onAfterChange={() => { loadEmpresas(); loadInter(); loadContactos(); }}
          onNewContacto={(empresa_id) => { setEmpresaOpen(null); setContactoForm({ empresa_id }); }}
          onNewInteraccion={(empresa_id) => { setEmpresaOpen(null); setInterForm({ empresa_id }); }}
        />
      )}
      {empresaForm && (
        <EmpresaFormDialog
          initial={empresaForm}
          catalogo={catalogo}
          usuarios={usuarios}
          onClose={() => setEmpresaForm(null)}
          onSaved={() => { setEmpresaForm(null); loadEmpresas(); }}
        />
      )}
      {contactoOpen && (
        <FichaContacto
          contactoId={contactoOpen}
          canEdit={canEdit}
          catalogo={catalogo}
          usuariosIndex={usuariosIndex}
          onClose={() => setContactoOpen(null)}
          onEdit={(c) => { setContactoOpen(null); setContactoForm(c); }}
          onAfterChange={loadContactos}
          onNewInteraccion={(empresa_id, contacto_id) => { setContactoOpen(null); setInterForm({ empresa_id, contacto_id }); }}
        />
      )}
      {contactoForm && (
        <ContactoFormDialog
          initial={contactoForm}
          empresas={empresas}
          catalogo={catalogo}
          usuarios={usuarios}
          onClose={() => setContactoForm(null)}
          onSaved={() => { setContactoForm(null); loadContactos(); }}
        />
      )}
      {interForm && (
        <InteraccionFormDialog
          initial={interForm}
          empresas={empresas}
          catalogo={catalogo}
          currentUser={user}
          onClose={() => setInterForm(null)}
          onSaved={() => { setInterForm(null); loadInter(); loadGeneral(); }}
        />
      )}
      {reportOpen && (
        <ReportDialog
          tipo={reportOpen.tipo}
          filtros={reportOpen.filtros || {}}
          onClose={() => setReportOpen(null)}
        />
      )}
    </div>
  );
}

// ═══════════════ Ficha Empresa ═══════════════
function FichaEmpresa({ empresaId, canEdit, catalogo, usuariosIndex, onClose, onEdit, onAfterChange, onNewContacto, onNewInteraccion }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notaTxt, setNotaTxt] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/directorio/empresas/${empresaId}`, { withCredentials: true });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [empresaId]);

  const addNota = async () => {
    if (!notaTxt.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/directorio/empresas/${empresaId}/notas`, { texto: notaTxt.trim() }, { withCredentials: true });
      setNotaTxt("");
      await load();
      onAfterChange && onAfterChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar la nota"); }
    finally { setSaving(false); }
  };

  const delNota = async (nid) => {
    if (!window.confirm("¿Eliminar esta nota?")) return;
    try {
      await axios.delete(`${API}/directorio/empresas/${empresaId}/notas/${nid}`, { withCredentials: true });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo borrar"); }
  };

  const eliminarEmpresa = async () => {
    if (!window.confirm("¿Eliminar la empresa y sus contactos/interacciones? Esta acción no se puede deshacer.")) return;
    try {
      await axios.delete(`${API}/directorio/empresas/${empresaId}?cascade=true`, { withCredentials: true });
      toast.success("Empresa eliminada");
      onAfterChange && onAfterChange();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="ficha-empresa">
        {loading || !data ? (
          <div className="flex items-center gap-2 p-8"><Loader2 className="w-5 h-5 animate-spin" /> Cargando…</div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                {data.empresa.logo_url ? (
                  <div className="w-14 h-14 bg-slate-50 rounded flex items-center justify-center overflow-hidden border">
                    <img src={data.empresa.logo_url} alt="" className="max-w-full max-h-full object-contain" />
                  </div>
                ) : (
                  <div className="w-14 h-14 bg-slate-100 rounded flex items-center justify-center text-slate-400"><Building2 className="w-6 h-6" /></div>
                )}
                <div className="flex-1">
                  <div className="text-xl font-bold text-slate-900">{data.empresa.nombre}</div>
                  {data.empresa.razon_social && <div className="text-xs text-slate-500">{data.empresa.razon_social}</div>}
                </div>
                <Badge className={`${estatusColor[data.empresa.estatus] || ""}`}>{data.empresa.estatus}</Badge>
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              {/* Datos */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2 text-sm">
                <div><span className="text-slate-500">Tipo:</span> <b>{data.empresa.tipo_relacion || "—"}</b></div>
                <div><span className="text-slate-500">Categoría:</span> <b>{data.empresa.categoria || "—"}</b></div>
                <div><span className="text-slate-500">Nivel:</span> <b>{data.empresa.nivel_relacion || "—"}</b></div>
                <div><span className="text-slate-500">País:</span> <b>{data.empresa.pais || "—"}</b></div>
                <div><span className="text-slate-500">Ciudad:</span> <b>{data.empresa.ciudad || "—"}</b></div>
                <div><span className="text-slate-500">Cobertura:</span> <b>{data.empresa.cobertura || "—"}</b></div>
                <div><span className="text-slate-500">Responsable PD:</span> <b>{data.empresa.responsable_pd || "—"}</b></div>
                <div><span className="text-slate-500">Origen:</span> <b>{data.empresa.origen_contacto || "—"}</b></div>
                {data.empresa.web && <div className="flex items-center gap-1"><Globe className="w-3 h-3 text-slate-400" /><a href={data.empresa.web} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline truncate">{data.empresa.web}</a></div>}
                {data.empresa.telefono && <div className="flex items-center gap-1"><Phone className="w-3 h-3 text-slate-400" />{formatPhone(data.empresa.telefono)}</div>}
              </div>

              {data.empresa.especialidades?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold uppercase text-slate-500 mb-1">Especialidades</p>
                  <div className="flex flex-wrap gap-1">{data.empresa.especialidades.map((s) => <Badge key={s} variant="outline">{s}</Badge>)}</div>
                </div>
              )}

              {data.empresa.notas_generales && (
                <div>
                  <p className="text-xs font-semibold uppercase text-slate-500 mb-1">Notas generales</p>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{data.empresa.notas_generales}</p>
                </div>
              )}

              {/* Contactos */}
              <div className="border-t pt-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold uppercase text-slate-500">Contactos ({data.contactos.length})</p>
                  {canEdit && <Button variant="outline" size="sm" onClick={() => onNewContacto(data.empresa.id)} data-testid="ficha-emp-nuevo-contacto"><Plus className="w-3.5 h-3.5 mr-1" /> Contacto</Button>}
                </div>
                {data.contactos.length === 0 && <p className="text-sm text-slate-400 italic">Aún no hay contactos registrados</p>}
                <div className="space-y-1.5">
                  {data.contactos.map((c) => (
                    <div key={c.id} className="flex items-center gap-2 border rounded p-2 bg-slate-50">
                      {c.foto_url ? <img src={c.foto_url} alt="" className="w-8 h-8 rounded-full object-cover" /> : <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center"><User className="w-4 h-4 text-slate-400" /></div>}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate flex items-center gap-1">
                          {c.es_principal && <Star className="w-3 h-3 text-amber-500 fill-amber-500 shrink-0" />}
                          {[c.nombre, c.apellidos].filter(Boolean).join(" ")}
                          <span className="text-xs text-slate-500 font-normal">· {c.puesto || "—"}</span>
                        </div>
                        <div className="text-xs text-slate-500 truncate">{c.correo || "—"} · {formatPhone(c.telefono) || formatPhone(c.whatsapp) || "—"}</div>
                      </div>
                      <Badge className={`${estatusColor[c.estatus] || ""} text-[10px]`}>{c.estatus}</Badge>
                    </div>
                  ))}
                </div>
              </div>

              {/* Historial */}
              <div className="border-t pt-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold uppercase text-slate-500">Historial ({data.interacciones.length})</p>
                  <Button variant="outline" size="sm" onClick={() => onNewInteraccion(data.empresa.id)} data-testid="ficha-emp-nueva-inter"><Plus className="w-3.5 h-3.5 mr-1" /> Registrar interacción</Button>
                </div>
                {data.interacciones.length === 0 && <p className="text-sm text-slate-400 italic">Sin interacciones registradas</p>}
                <div className="space-y-1.5">
                  {data.interacciones.map((i) => (
                    <div key={i.id} className="border-l-2 border-blue-500 pl-2.5 py-1">
                      <div className="text-xs text-slate-500">{i.fecha} · <b className="text-slate-700">{i.tipo}</b> · {i.registrado_por}</div>
                      <div className="text-sm text-slate-700">{i.nota}</div>
                      {i.proyecto && <div className="text-xs text-slate-500">Proyecto: {i.proyecto}</div>}
                      {i.proximo_paso && <div className="text-xs text-amber-700">→ {i.proximo_paso}</div>}
                    </div>
                  ))}
                </div>
              </div>

              {/* Notas internas */}
              <div className="border-t pt-3">
                <p className="text-xs font-semibold uppercase text-slate-500 mb-2 flex items-center gap-1"><StickyNote className="w-3 h-3" /> Notas internas ({(data.empresa.notas_internas || []).length})</p>
                {canEdit && (
                  <div className="flex gap-2 mb-2">
                    <Input placeholder="Nueva nota…" value={notaTxt} onChange={(e) => setNotaTxt(e.target.value)} data-testid="ficha-emp-nota-input" />
                    <Button onClick={addNota} disabled={saving || !notaTxt.trim()}>Guardar</Button>
                  </div>
                )}
                <div className="space-y-1.5">
                  {(data.empresa.notas_internas || []).map((n) => (
                    <div key={n.id} className="text-sm border rounded p-2 bg-amber-50 border-amber-200 flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <div className="text-slate-700">{n.texto}</div>
                        <div className="text-[10px] text-slate-500 mt-1">{n.autor} · {formatDate(n.fecha)}</div>
                      </div>
                      {canEdit && <button onClick={() => delNota(n.id)} className="text-slate-400 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <DialogFooter className="border-t pt-3 flex justify-between">
              <div>
                {canEdit && <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={eliminarEmpresa} data-testid="ficha-emp-eliminar"><Trash2 className="w-4 h-4 mr-1" /> Eliminar</Button>}
              </div>
              <div className="flex gap-2">
                {canEdit && <Button onClick={() => onEdit(data.empresa)} data-testid="ficha-emp-editar"><Edit2 className="w-4 h-4 mr-1" /> Editar</Button>}
                <Button variant="outline" onClick={onClose}>Cerrar</Button>
              </div>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════ Ficha Contacto ═══════════════
function FichaContacto({ contactoId, canEdit, catalogo, usuariosIndex, onClose, onEdit, onAfterChange, onNewInteraccion }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/directorio/contactos/${contactoId}`, { withCredentials: true });
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [contactoId]);

  const patchEstatus = async (nuevo) => {
    try {
      await axios.patch(`${API}/directorio/contactos/${contactoId}/estatus`, { estatus: nuevo }, { withCredentials: true });
      toast.success(`Estatus actualizado a ${nuevo}`);
      await load();
      onAfterChange && onAfterChange();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.campos_faltantes) {
        toast.error(`${d.message}. Faltan: ${d.campos_faltantes.join(", ")}`);
      } else { toast.error(d || "Error"); }
    }
  };

  const eliminarContacto = async () => {
    if (!window.confirm("¿Eliminar este contacto?")) return;
    try {
      await axios.delete(`${API}/directorio/contactos/${contactoId}`, { withCredentials: true });
      toast.success("Contacto eliminado");
      onAfterChange && onAfterChange();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="ficha-contacto">
        {loading || !data ? (
          <div className="flex items-center gap-2 p-8"><Loader2 className="w-5 h-5 animate-spin" /> Cargando…</div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                {data.contacto.foto_url ? (
                  <div className="w-14 h-14 rounded-full overflow-hidden border"><img src={data.contacto.foto_url} alt="" className="w-full h-full object-cover" /></div>
                ) : (
                  <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-slate-400"><User className="w-6 h-6" /></div>
                )}
                <div className="flex-1">
                  <div className="text-xl font-bold text-slate-900 flex items-center gap-2">
                    {data.contacto.es_principal && <Star className="w-4 h-4 text-amber-500 fill-amber-500" />}
                    {[data.contacto.nombre, data.contacto.apellidos].filter(Boolean).join(" ")}
                  </div>
                  <div className="text-xs text-slate-500">{data.contacto.puesto || "—"} · {data.empresa?.nombre || "—"}</div>
                </div>
                <Badge className={`${estatusColor[data.contacto.estatus] || ""}`}>{data.contacto.estatus}</Badge>
              </DialogTitle>
            </DialogHeader>

            {data.campos_faltantes?.length > 0 && (
              <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm">
                <div className="flex items-center gap-2 text-red-700 font-semibold mb-1"><AlertTriangle className="w-4 h-4" /> Datos incompletos</div>
                <p className="text-red-600 text-xs">Faltan: <b>{data.campos_faltantes.join(", ")}</b>. Completa la información para poder activar el contacto.</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-slate-400" /> {data.contacto.correo || "—"}</div>
              <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-slate-400" /> {formatPhone(data.contacto.telefono) || "—"}</div>
              <div className="flex items-center gap-2"><MessageCircle className="w-3.5 h-3.5 text-slate-400" /> {formatPhone(data.contacto.whatsapp) || "—"}</div>
              <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-slate-400" /> {[data.contacto.ciudad, data.contacto.pais].filter(Boolean).join(", ") || "—"}</div>
              <div><span className="text-slate-500">Área:</span> <b>{data.contacto.area || "—"}</b></div>
              <div><span className="text-slate-500">Tipo:</span> <b>{data.contacto.tipo_relacion || "—"}</b></div>
              <div><span className="text-slate-500">Responsable PD:</span> <b>{data.contacto.responsable_pd || "—"}</b></div>
              <div><span className="text-slate-500">Mailchimp:</span> <b>{data.contacto.mailchimp_status || "—"}</b></div>
            </div>

            <div className="border-t pt-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold uppercase text-slate-500">Historial del contacto ({data.interacciones.length})</p>
                {onNewInteraccion && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onNewInteraccion(data.empresa?.id || data.contacto.empresa_id, data.contacto.id)}
                    data-testid="ficha-contacto-nueva-inter"
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" /> Registrar interacción
                  </Button>
                )}
              </div>
              {data.interacciones.length === 0 && <p className="text-sm text-slate-400 italic">Sin interacciones registradas con este contacto</p>}
              <div className="space-y-1.5">
                {data.interacciones.map((i) => (
                  <div key={i.id} className="border-l-2 border-blue-500 pl-2.5 py-1">
                    <div className="text-xs text-slate-500">{i.fecha} · <b>{i.tipo}</b> · {i.registrado_por}</div>
                    <div className="text-sm text-slate-700">{i.nota}</div>
                  </div>
                ))}
              </div>
            </div>

            <DialogFooter className="border-t pt-3 flex justify-between">
              <div className="flex gap-2">
                {canEdit && <Button variant="outline" className="text-red-600 border-red-200" onClick={eliminarContacto}><Trash2 className="w-4 h-4 mr-1" /> Eliminar</Button>}
              </div>
              <div className="flex gap-2">
                {canEdit && data.contacto.estatus === "Activo" && <Button variant="outline" onClick={() => patchEstatus("Inactivo")}>Desactivar</Button>}
                {canEdit && data.contacto.estatus === "Inactivo" && <Button variant="outline" onClick={() => patchEstatus("Activo")}>Activar</Button>}
                {canEdit && data.contacto.estatus === "Datos incompletos" && (
                  <Button variant="outline" onClick={() => patchEstatus("Activo")} title="Solo funciona cuando ya no faltan datos">Intentar activar</Button>
                )}
                {canEdit && <Button onClick={() => onEdit(data.contacto)}><Edit2 className="w-4 h-4 mr-1" /> Editar</Button>}
                <Button variant="outline" onClick={onClose}>Cerrar</Button>
              </div>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════ Forms ═══════════════
function ImageUpload({ kind, currentUrl, onUploaded, testid, fit = "contain" }) {
  const [uploading, setUploading] = useState(false);
  const handle = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) { toast.error("Máx 2 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await axios.post(`${API}/directorio/upload/${kind}`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(r.data.url);
      toast.success("Imagen subida");
    } catch (er) { toast.error(er?.response?.data?.detail || "Error"); }
    finally { setUploading(false); e.target.value = ""; }
  };
  return (
    <div className="flex items-center gap-3">
      <div className={`w-16 h-16 rounded ${kind === "foto" ? "rounded-full" : ""} border bg-slate-50 flex items-center justify-center overflow-hidden shrink-0`}>
        {currentUrl ? (
          <img src={currentUrl} alt="" className={fit === "contain" ? "max-w-full max-h-full object-contain" : "w-full h-full object-cover"} />
        ) : (
          <ImageIcon className="w-6 h-6 text-slate-300" />
        )}
      </div>
      <div className="flex-1">
        <label className="cursor-pointer inline-flex items-center gap-1.5 text-sm text-blue-600 hover:underline">
          <Upload className="w-3.5 h-3.5" />
          {uploading ? "Subiendo…" : (currentUrl ? "Cambiar imagen" : "Subir imagen")}
          <input type="file" className="hidden" accept="image/jpeg,image/png,image/webp,image/svg+xml" onChange={handle} data-testid={testid} disabled={uploading} />
        </label>
        <p className="text-[11px] text-slate-400">JPG, PNG, WEBP, SVG · máx 2 MB</p>
      </div>
      {currentUrl && (
        <Button variant="ghost" size="sm" onClick={() => onUploaded("")}><X className="w-3.5 h-3.5" /></Button>
      )}
    </div>
  );
}

function EmpresaFormDialog({ initial, catalogo, usuarios, onClose, onSaved }) {
  const editing = !!initial.id;
  const [f, setF] = useState({
    nombre: "", razon_social: "", logo_url: "", tipo_relacion: "", categoria: "", especialidades: [],
    pais: "", ciudad: "", cobertura: "", web: "", telefono: "", estatus: "Activo",
    nivel_relacion: "", origen_contacto: "", responsable_pd: "", notas_generales: "", ...initial,
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!f.nombre.trim()) { toast.error("El nombre es obligatorio"); return; }
    setSaving(true);
    try {
      if (editing) await axios.put(`${API}/directorio/empresas/${initial.id}`, f, { withCredentials: true });
      else await axios.post(`${API}/directorio/empresas`, f, { withCredentials: true });
      toast.success(editing ? "Empresa actualizada" : "Empresa creada");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const set = (k, v) => setF({ ...f, [k]: v });
  const toggleEsp = (opt) => set("especialidades", f.especialidades.includes(opt) ? f.especialidades.filter(x => x !== opt) : [...f.especialidades, opt]);

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="empresa-form">
        <DialogHeader>
          <DialogTitle>{editing ? "Editar empresa" : "Nueva empresa"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Logo</Label>
            <ImageUpload kind="logo" currentUrl={f.logo_url} onUploaded={(u) => set("logo_url", u)} testid="upload-logo" fit="contain" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Nombre comercial *</Label><Input value={f.nombre} onChange={(e) => set("nombre", e.target.value)} data-testid="emp-nombre" /></div>
            <div><Label>Razón social</Label><Input value={f.razon_social} onChange={(e) => set("razon_social", e.target.value)} /></div>
            <div>
              <Label>Tipo de relación</Label>
              <Select value={f.tipo_relacion || "__none__"} onValueChange={(v) => set("tipo_relacion", v === "__none__" ? "" : v)}>
                <SelectTrigger data-testid="emp-tipo"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="__none__">—</SelectItem>{catalogo.tipo_relacion.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Categoría</Label>
              <Select value={f.categoria || "__none__"} onValueChange={(v) => set("categoria", v === "__none__" ? "" : v)}>
                <SelectTrigger data-testid="emp-cat"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="__none__">—</SelectItem>{catalogo.categoria.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>País</Label><Input value={f.pais} onChange={(e) => set("pais", e.target.value)} /></div>
            <div><Label>Ciudad / destino</Label><Input value={f.ciudad} onChange={(e) => set("ciudad", e.target.value)} /></div>
            <div>
              <Label>Cobertura</Label>
              <Select value={f.cobertura || "__none__"} onValueChange={(v) => set("cobertura", v === "__none__" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="__none__">—</SelectItem>{catalogo.cobertura.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Nivel de relación</Label>
              <Select value={f.nivel_relacion || "__none__"} onValueChange={(v) => set("nivel_relacion", v === "__none__" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="__none__">—</SelectItem>{catalogo.nivel_relacion.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Origen del contacto</Label>
              <Select value={f.origen_contacto || "__none__"} onValueChange={(v) => set("origen_contacto", v === "__none__" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="__none__">—</SelectItem>{catalogo.origen_contacto.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Estatus</Label>
              <Select value={f.estatus} onValueChange={(v) => set("estatus", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{catalogo.estatus_empresa.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Web</Label><Input value={f.web} onChange={(e) => set("web", e.target.value)} placeholder="https://" /></div>
            <div><PhoneField label="Teléfono" value={f.telefono} onChange={(v) => set("telefono", v)} testid="emp-tel" /></div>
            <div className="col-span-2">
              <Label>Responsable PD</Label>
              <Select value={f.responsable_pd || "__none__"} onValueChange={(v) => set("responsable_pd", v === "__none__" ? "" : v)}>
                <SelectTrigger data-testid="emp-responsable-pd"><SelectValue placeholder="Selecciona la persona de PD" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Sin asignar</SelectItem>
                  {(usuarios || []).map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Especialidades</Label>
            <div className="flex flex-wrap gap-1 mt-1">
              {catalogo.especialidad.map((o) => (
                <button key={o} type="button" onClick={() => toggleEsp(o)}
                  className={`text-xs px-2 py-1 rounded-full border ${f.especialidades.includes(o) ? "bg-blue-100 border-blue-300 text-blue-700" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"}`}
                  data-testid={`emp-esp-${o}`}>{o}</button>
              ))}
            </div>
          </div>
          <div><Label>Notas generales</Label><Textarea rows={3} value={f.notas_generales} onChange={(e) => set("notas_generales", e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} disabled={saving} data-testid="emp-form-save">{saving ? "Guardando…" : (editing ? "Actualizar" : "Crear")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ContactoFormDialog({ initial, empresas, catalogo, usuarios, onClose, onSaved }) {
  const editing = !!initial.id;
  const [f, setF] = useState({
    empresa_id: "", nombre: "", apellidos: "", puesto: "", area: "", foto_url: "",
    correo: "", telefono: "", whatsapp: "", ciudad: "", pais: "",
    tipo_relacion: "", responsable_pd: "", es_principal: false, estatus: "Activo",
    cumpleanos_fecha: "", cumpleanos_incluye_ano: false,
    ...initial,
  });
  const [saving, setSaving] = useState(false);
  const [complianceOpen, setComplianceOpen] = useState(false);
  // Cumpleaños: 3 sub-campos locales, se componen a cumpleanos_fecha (YYYY-MM-DD) al guardar
  const _initParsed = /^(\d{4})-(\d{2})-(\d{2})$/.exec(initial.cumpleanos_fecha || "");
  const [cumpleDia, setCumpleDia] = useState(_initParsed ? _initParsed[3] : "");
  const [cumpleMes, setCumpleMes] = useState(_initParsed ? _initParsed[2] : "");
  const [cumpleAnio, setCumpleAnio] = useState(_initParsed && initial.cumpleanos_incluye_ano ? _initParsed[1] : "");

  // Sync automático de los 3 sub-campos → f.cumpleanos_fecha
  useEffect(() => {
    const dd = /^\d{1,2}$/.test(cumpleDia) ? String(parseInt(cumpleDia, 10)).padStart(2, "0") : "";
    const mm = /^\d{1,2}$/.test(cumpleMes) ? String(parseInt(cumpleMes, 10)).padStart(2, "0") : "";
    const yy = /^\d{4}$/.test(cumpleAnio) ? cumpleAnio : "";
    if (dd && mm) {
      const yearPart = yy || "0001"; // 0001 = año desconocido
      setF((prev) => ({ ...prev, cumpleanos_fecha: `${yearPart}-${mm}-${dd}`, cumpleanos_incluye_ano: !!yy }));
    } else {
      setF((prev) => ({ ...prev, cumpleanos_fecha: "", cumpleanos_incluye_ano: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cumpleDia, cumpleMes, cumpleAnio]);

  const doSave = async () => {
    setSaving(true);
    try {
      if (editing) await axios.put(`${API}/directorio/contactos/${initial.id}`, f, { withCredentials: true });
      else await axios.post(`${API}/directorio/contactos`, f, { withCredentials: true });
      toast.success(editing ? "Contacto actualizado" : "Contacto creado");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setSaving(false); setComplianceOpen(false); }
  };

  const save = () => {
    if (!f.empresa_id) { toast.error("Elige la empresa"); return; }
    if (!f.nombre.trim()) { toast.error("El nombre es obligatorio"); return; }
    // Compliance: si tipo_relacion = "Cliente de Terceros", abrir confirmación
    if (f.tipo_relacion === "Cliente de Terceros") {
      setComplianceOpen(true);
      return;
    }
    doSave();
  };
  const set = (k, v) => setF({ ...f, [k]: v });

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="contacto-form">
        <DialogHeader>
          <DialogTitle>{editing ? "Editar contacto" : "Nuevo contacto"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Foto</Label><ImageUpload kind="foto" currentUrl={f.foto_url} onUploaded={(u) => set("foto_url", u)} testid="upload-foto" fit="cover" /></div>
          <div>
            <Label>Empresa *</Label>
            <Select value={f.empresa_id || "__none__"} onValueChange={(v) => set("empresa_id", v === "__none__" ? "" : v)} disabled={editing}>
              <SelectTrigger data-testid="ct-empresa"><SelectValue placeholder="Elige una empresa" /></SelectTrigger>
              <SelectContent><SelectItem value="__none__" disabled>Elige…</SelectItem>{empresas.map((e) => <SelectItem key={e.id} value={e.id}>{e.nombre}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Nombre *</Label><Input value={f.nombre} onChange={(e) => set("nombre", e.target.value)} data-testid="ct-nombre" /></div>
            <div><Label>Apellidos</Label><Input value={f.apellidos} onChange={(e) => set("apellidos", e.target.value)} /></div>
            <div><Label>Puesto</Label><Input value={f.puesto} onChange={(e) => set("puesto", e.target.value)} placeholder="Obligatorio para 'Activo'" data-testid="ct-puesto" /></div>
            <div><Label>Área</Label><Input value={f.area} onChange={(e) => set("area", e.target.value)} placeholder="Ventas, Grupos…" /></div>
            <div><Label>Correo</Label><Input type="email" value={f.correo} onChange={(e) => set("correo", e.target.value)} placeholder="Obligatorio para 'Activo'" data-testid="ct-correo" /></div>
            <div><PhoneField label="Teléfono" value={f.telefono} onChange={(v) => set("telefono", v)} placeholder="Teléfono o WhatsApp obligatorio" testid="ct-tel" /></div>
            <div><PhoneField label="WhatsApp" value={f.whatsapp} onChange={(v) => set("whatsapp", v)} testid="ct-wa" /></div>
            <div>
              <Label>Tipo de relación</Label>
              <Select value={f.tipo_relacion || "__none__"} onValueChange={(v) => set("tipo_relacion", v === "__none__" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="__none__">—</SelectItem>{catalogo.tipo_relacion.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Ciudad</Label><Input value={f.ciudad} onChange={(e) => set("ciudad", e.target.value)} /></div>
            <div><Label>País</Label><Input value={f.pais} onChange={(e) => set("pais", e.target.value)} /></div>
            <div>
              <Label>Responsable PD</Label>
              <Select value={f.responsable_pd || "__none__"} onValueChange={(v) => set("responsable_pd", v === "__none__" ? "" : v)}>
                <SelectTrigger data-testid="ct-responsable-pd"><SelectValue placeholder="Selecciona la persona de PD" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Sin asignar</SelectItem>
                  {(usuarios || []).map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Estatus (deseado)</Label>
              <Select value={["Activo", "Inactivo"].includes(f.estatus) ? f.estatus : "Activo"} onValueChange={(v) => set("estatus", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Activo">Activo</SelectItem>
                  <SelectItem value="Inactivo">Inactivo</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500 mt-1">Si faltan datos obligatorios, se guarda como "Datos incompletos".</p>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={!!f.es_principal} onChange={(e) => set("es_principal", e.target.checked)} data-testid="ct-principal" />
            <span>Marcar como contacto principal de la empresa</span>
          </label>
          {/* Cumpleaños */}
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-2">
            <p className="text-xs font-semibold uppercase text-slate-600">Cumpleaños (opcional)</p>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Día</Label>
                <select
                  className="w-full h-9 rounded-md border border-slate-200 bg-white text-sm px-2"
                  value={cumpleDia}
                  onChange={(e) => setCumpleDia(e.target.value)}
                  data-testid="ct-cumple-dia"
                >
                  <option value="">—</option>
                  {Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, "0")).map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-xs">Mes</Label>
                <select
                  className="w-full h-9 rounded-md border border-slate-200 bg-white text-sm px-2"
                  value={cumpleMes}
                  onChange={(e) => setCumpleMes(e.target.value)}
                  data-testid="ct-cumple-mes"
                >
                  <option value="">—</option>
                  {[["01","Enero"],["02","Febrero"],["03","Marzo"],["04","Abril"],["05","Mayo"],["06","Junio"],["07","Julio"],["08","Agosto"],["09","Septiembre"],["10","Octubre"],["11","Noviembre"],["12","Diciembre"]].map(([v, l]) => (
                    <option key={v} value={v}>{l}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-xs">Año <span className="text-slate-400">(opcional)</span></Label>
                <Input
                  type="number"
                  min="1900"
                  max="2099"
                  placeholder="—"
                  value={cumpleAnio}
                  onChange={(e) => setCumpleAnio(e.target.value)}
                  data-testid="ct-cumple-anio"
                />
              </div>
            </div>
            <p className="text-[11px] text-slate-500">Si el contacto no quiere dar su año, deja el año vacío. Solo se usará día y mes para las campañas de cumpleaños.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} disabled={saving} data-testid="ct-form-save">{saving ? "Guardando…" : (editing ? "Actualizar" : "Crear")}</Button>
        </DialogFooter>
      </DialogContent>

      {/* Compliance dialog: Cliente de Terceros */}
      {complianceOpen && (
        <Dialog open={true} onOpenChange={(o) => !o && setComplianceOpen(false)}>
          <DialogContent className="max-w-md" data-testid="compliance-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-700">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                Cumplimiento normativo
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm text-slate-700">
              <p>
                Este contacto se guardará como <b>Cliente de Terceros</b>.
              </p>
              <p className="bg-amber-50 border border-amber-200 rounded-md p-3 text-slate-700">
                Para incluirlo en <b>campañas de correo</b> se requiere validación previa con el <b>área comercial</b> por temas de <b>cumplimiento normativo</b> (compliance).
              </p>
              <p className="text-xs text-slate-500">
                Al guardar, el contacto quedará registrado pero cada envío de correo por campaña necesitará autorización expresa de un supervisor o administrador.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setComplianceOpen(false)} data-testid="compliance-cancel">
                Cancelar
              </Button>
              <Button onClick={doSave} disabled={saving} className="bg-amber-600 hover:bg-amber-700" data-testid="compliance-confirm">
                {saving ? "Guardando…" : "Guardar de todas formas"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  );
}

function InteraccionFormDialog({ initial, empresas, catalogo, currentUser, onClose, onSaved }) {
  const lockEmpresa = !!initial?.empresa_id;
  const lockContacto = !!initial?.contacto_id;
  const [f, setF] = useState({
    empresa_id: "",
    contacto_id: "",
    fecha: new Date().toISOString().slice(0, 10),
    tipo: "",
    proyecto: "",
    nota: "",
    proximo_paso: "",
    registrado_por: currentUser?.name || currentUser?.email || "",
    ...initial,
  });
  const [contactosEmpresa, setContactosEmpresa] = useState([]);
  const [saving, setSaving] = useState(false);

  // Cargar contactos filtrados por empresa
  useEffect(() => {
    if (!f.empresa_id) { setContactosEmpresa([]); return; }
    (async () => {
      try {
        const r = await axios.get(`${API}/directorio/contactos?empresa_id=${f.empresa_id}&page_size=200`, { withCredentials: true });
        setContactosEmpresa(r.data.items || []);
      } catch { setContactosEmpresa([]); }
    })();
  }, [f.empresa_id]);

  const empresaSel = useMemo(() => empresas.find((e) => e.id === f.empresa_id), [empresas, f.empresa_id]);
  const contactoSel = useMemo(() => contactosEmpresa.find((c) => c.id === f.contacto_id), [contactosEmpresa, f.contacto_id]);

  const notaLimpia = (f.nota || "").trim();
  const disabled = saving || !f.empresa_id || !f.fecha || !notaLimpia;

  const save = async () => {
    if (disabled) return;
    setSaving(true);
    try {
      const payload = {
        empresa_id: f.empresa_id,
        contacto_id: f.contacto_id || null,
        fecha: f.fecha,
        tipo: f.tipo || "",
        proyecto: f.proyecto || "",
        nota: notaLimpia,
        proximo_paso: f.proximo_paso || "",
        registrado_por: f.registrado_por || "",
      };
      await axios.post(`${API}/directorio/interacciones`, payload, { withCredentials: true });
      toast.success("Interacción registrada");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };
  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="inter-form">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-[#0037ff]" />
            Registrar interacción
          </DialogTitle>
          <p className="text-xs text-slate-500">
            Deja constancia de reuniones, cotizaciones, visitas u otros contactos con la empresa. Aparecerá al instante en el historial global y en las fichas relacionadas.
          </p>
        </DialogHeader>
        <div className="space-y-3">
          {/* Empresa */}
          <div>
            <Label className="text-xs">Empresa *</Label>
            {lockEmpresa ? (
              <div className="mt-1 flex items-center gap-2 rounded-md border bg-slate-50 px-3 py-2 text-sm" data-testid="int-empresa-locked">
                <Building2 className="w-4 h-4 text-slate-500 shrink-0" />
                <span className="font-medium text-slate-800 truncate">{empresaSel?.nombre || initial?.empresa_nombre || "Empresa preseleccionada"}</span>
                <Badge variant="outline" className="ml-auto text-[10px]">Bloqueada</Badge>
              </div>
            ) : (
              <Select value={f.empresa_id || "__none__"} onValueChange={(v) => { set("empresa_id", v === "__none__" ? "" : v); set("contacto_id", ""); }}>
                <SelectTrigger data-testid="int-empresa"><SelectValue placeholder="Elige una empresa" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__" disabled>Elige…</SelectItem>
                  {empresas.map((e) => <SelectItem key={e.id} value={e.id}>{e.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Contacto */}
          {f.empresa_id && (
            <div>
              <Label className="text-xs">Contacto (opcional)</Label>
              {lockContacto ? (
                <div className="mt-1 flex items-center gap-2 rounded-md border bg-slate-50 px-3 py-2 text-sm" data-testid="int-contacto-locked">
                  <User className="w-4 h-4 text-slate-500 shrink-0" />
                  <span className="font-medium text-slate-800 truncate">
                    {contactoSel ? [contactoSel.nombre, contactoSel.apellidos].filter(Boolean).join(" ") + (contactoSel.puesto ? ` — ${contactoSel.puesto}` : "") : "Contacto preseleccionado"}
                  </span>
                  <Badge variant="outline" className="ml-auto text-[10px]">Bloqueado</Badge>
                </div>
              ) : (
                <Select value={f.contacto_id || "__none__"} onValueChange={(v) => set("contacto_id", v === "__none__" ? "" : v)}>
                  <SelectTrigger data-testid="int-contacto"><SelectValue placeholder="Sin contacto específico" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Sin contacto específico</SelectItem>
                    {contactosEmpresa.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {[c.nombre, c.apellidos].filter(Boolean).join(" ")}{c.puesto ? ` — ${c.puesto}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {!lockContacto && contactosEmpresa.length === 0 && (
                <p className="text-[11px] text-slate-400 mt-1">Esta empresa aún no tiene contactos registrados.</p>
              )}
            </div>
          )}

          {/* Fecha + Tipo */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Fecha *</Label>
              <Input type="date" value={f.fecha} onChange={(e) => set("fecha", e.target.value)} data-testid="int-fecha" />
            </div>
            <div>
              <Label className="text-xs">Tipo de interacción</Label>
              <Select value={f.tipo || "__none__"} onValueChange={(v) => set("tipo", v === "__none__" ? "" : v)}>
                <SelectTrigger data-testid="int-tipo"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Sin tipo</SelectItem>
                  {(catalogo.tipo_interaccion || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Persona PD */}
          <div>
            <Label className="text-xs">Persona de PD que registra</Label>
            <Input
              value={f.registrado_por}
              onChange={(e) => set("registrado_por", e.target.value)}
              placeholder="Tu nombre"
              data-testid="int-registrado-por"
            />
          </div>

          {/* Proyecto */}
          <div>
            <Label className="text-xs">Proyecto relacionado (opcional)</Label>
            <Input value={f.proyecto} onChange={(e) => set("proyecto", e.target.value)} placeholder="Nombre del proyecto o cotización" data-testid="int-proyecto" />
          </div>

          {/* Nota */}
          <div>
            <Label className="text-xs">Nota breve *</Label>
            <Textarea rows={3} value={f.nota} onChange={(e) => set("nota", e.target.value)} placeholder="¿Qué pasó en la interacción?" data-testid="int-nota" />
          </div>

          {/* Próximo paso */}
          <div>
            <Label className="text-xs">Próximo paso (opcional)</Label>
            <Input value={f.proximo_paso} onChange={(e) => set("proximo_paso", e.target.value)} placeholder="Ej. enviar propuesta el viernes" data-testid="int-proximo-paso" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button
            onClick={save}
            disabled={disabled}
            className="bg-[#0037ff] hover:bg-[#002bcc] disabled:opacity-50"
            data-testid="int-form-save"
          >
            {saving ? "Guardando…" : "Registrar interacción"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════ CAMPAÑAS PANEL ═══════════════
function CampanasPanel({ canEdit, catalogo, empresas, filters, setFilters, onDownload }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [wizard, setWizard] = useState(null); // {step, campana}
  const [detalle, setDetalle] = useState(null);
  const [busy, setBusy] = useState(null); // id de la campaña que está en operación

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/directorio/campanas`, { withCredentials: true });
      setItems(r.data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const duplicar = async (c) => {
    setBusy(c.id);
    try {
      const r = await axios.post(`${API}/directorio/campanas/${c.id}/duplicar`, {}, { withCredentials: true });
      toast.success(`Copia creada: ${r.data.nombre}`);
      await load();
      // Abrir el wizard con la copia para que el usuario ajuste destinatarios
      setWizard({ step: 1, campana: r.data });
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setBusy(null); }
  };

  const cancelar = async (c) => {
    if (!window.confirm(`¿Cancelar la campaña "${c.nombre}"? Esta acción no se puede deshacer.`)) return;
    setBusy(c.id);
    try {
      const r = await axios.post(`${API}/directorio/campanas/${c.id}/cancelar`, {}, { withCredentials: true });
      toast.success(r.data.message || "Campaña cancelada");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setBusy(null); }
  };

  const enviarCumples = async (c) => {
    if (!window.confirm(`Enviar el correo de "${c.nombre}" a los contactos cuyo cumpleaños sea HOY?`)) return;
    setBusy(c.id);
    try {
      const r = await axios.post(`${API}/directorio/campanas/${c.id}/enviar-cumpleanos-hoy`, {}, { withCredentials: true });
      toast.success(r.data.detail || `Envío hecho a ${r.data.enviados || 0} cumpleañeros`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setBusy(null); }
  };

  // Filtro cliente-side (los campos del backend no exponen filtros query)
  const filtered = useMemo(() => {
    const f = filters || { q: "", estados: [], fecha_desde: "", fecha_hasta: "" };
    return items.filter((c) => {
      if (f.q) {
        const q = f.q.toLowerCase();
        if (!(c.nombre || "").toLowerCase().includes(q) && !(c.asunto || "").toLowerCase().includes(q)) return false;
      }
      if (f.estados && f.estados.length > 0 && !f.estados.includes(c.estatus)) return false;
      const created = (c.created_at || "").slice(0, 10);
      if (f.fecha_desde && created && created < f.fecha_desde) return false;
      if (f.fecha_hasta && created && created > f.fecha_hasta) return false;
      return true;
    });
  }, [items, filters]);

  const estadosOptions = ["borrador", "programada", "enviada", "cancelada", "error"];

  return (
    <div className="space-y-3">
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <div>
              <p className="text-sm text-slate-700 font-medium">Campañas de correo vía Mailchimp</p>
              <p className="text-xs text-slate-500">Los envíos NUNCA salen del buzón corporativo — todo pasa por Mailchimp</p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={onDownload}
                data-testid="btn-descargar-campanas"
                className="border-[#0037ff]/30 text-[#0037ff] hover:bg-[#0037ff]/5"
              >
                <Download className="w-4 h-4 mr-1.5" /> Descargar reporte
              </Button>
              {canEdit && (
                <Button onClick={() => setWizard({ step: 1, campana: { filtros: {}, reply_to: user?.email || "", from_name: user?.name || "PD Travel" } })} className="bg-[#0037ff] hover:bg-[#002bcc]" data-testid="btn-nueva-campana">
                  <Plus className="w-4 h-4 mr-1" /> Nueva campaña
                </Button>
              )}
            </div>
          </div>
          {/* Filtros */}
          {filters && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              <div className="space-y-1 md:col-span-2">
                <Label className="text-xs text-slate-600">Buscar</Label>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input className="pl-9" placeholder="Nombre o asunto…" value={filters.q}
                    onChange={(e) => setFilters({ ...filters, q: e.target.value })} data-testid="camp-filter-q" />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-600">Estado</Label>
                <MultiSelect label="" options={estadosOptions} value={filters.estados} onChange={(v) => setFilters({ ...filters, estados: v })} testid="camp-estados" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-600">Creada desde</Label>
                <Input type="date" value={filters.fecha_desde} onChange={(e) => setFilters({ ...filters, fecha_desde: e.target.value })} data-testid="camp-filter-desde" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-600">Creada hasta</Label>
                <Input type="date" value={filters.fecha_hasta} onChange={(e) => setFilters({ ...filters, fecha_hasta: e.target.value })} data-testid="camp-filter-hasta" />
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="[&>th]:py-2 [&>th]:text-xs [&>th]:font-semibold">
                <TableHead>Nombre</TableHead>
                <TableHead>Asunto</TableHead>
                <TableHead>Estatus</TableHead>
                <TableHead>Destinatarios</TableHead>
                <TableHead>Creada</TableHead>
                <TableHead>Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 && !loading && (
                <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8">
                  {items.length === 0 ? "Sin campañas" : "Ningún resultado con los filtros aplicados"}
                </TableCell></TableRow>
              )}
              {filtered.map((c) => (
                <TableRow key={c.id} className="[&>td]:py-1.5">
                  <TableCell className="text-sm font-medium">{c.nombre}</TableCell>
                  <TableCell className="text-sm max-w-[240px] truncate">{c.asunto || "—"}</TableCell>
                  <TableCell><Badge className={`${c.estatus === "enviada" ? "bg-emerald-100 text-emerald-700" : c.estatus === "programada" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-600"} text-[10px]`}>{c.estatus}</Badge></TableCell>
                  <TableCell className="text-sm">{c.resumen_envio?.destinatarios ?? "—"}</TableCell>
                  <TableCell className="text-xs text-slate-500">{formatDate(c.created_at)}</TableCell>
                  <TableCell className="whitespace-nowrap">
                    <div className="flex items-center gap-0.5">
                      <Button size="icon" variant="ghost" onClick={() => setDetalle(c)} data-testid={`camp-ver-${c.id}`} title="Ver detalle" className="h-8 w-8">
                        <Eye className="w-4 h-4 text-slate-500" />
                      </Button>
                      {c.estatus !== "enviada" && c.estatus !== "cancelada" && (
                        <Button size="icon" variant="ghost" onClick={() => setWizard({ step: 1, campana: c })} data-testid={`camp-editar-${c.id}`} title="Editar" className="h-8 w-8">
                          <Edit2 className="w-4 h-4 text-[#0037ff]" />
                        </Button>
                      )}
                      <Button size="icon" variant="ghost" onClick={() => duplicar(c)} data-testid={`camp-duplicar-${c.id}`} title="Duplicar como borrador" className="h-8 w-8" disabled={busy === c.id}>
                        {busy === c.id ? <Loader2 className="w-4 h-4 animate-spin text-slate-400" /> : <Copy className="w-4 h-4 text-emerald-600" />}
                      </Button>
                      {c.es_cumpleanos && c.estatus !== "cancelada" && (
                        <Button size="icon" variant="ghost" onClick={() => enviarCumples(c)} data-testid={`camp-cumples-${c.id}`} title="Enviar cumpleaños de hoy" className="h-8 w-8" disabled={busy === c.id}>
                          🎂
                        </Button>
                      )}
                      {(c.estatus === "borrador" || c.estatus === "programada") && (
                        <Button size="icon" variant="ghost" onClick={() => cancelar(c)} data-testid={`camp-cancelar-${c.id}`} title="Cancelar campaña" className="h-8 w-8" disabled={busy === c.id}>
                          <XCircle className="w-4 h-4 text-red-600" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {wizard && (
        <CampanaWizard
          catalogo={catalogo}
          initial={wizard.campana}
          startStep={wizard.step}
          onClose={() => setWizard(null)}
          onSaved={() => { setWizard(null); load(); }}
        />
      )}
      {detalle && (
        <CampanaDetalle
          campana={detalle}
          onClose={() => setDetalle(null)}
          onEdit={() => { const c = detalle; setDetalle(null); setWizard({ step: 1, campana: c }); }}
        />
      )}
    </div>
  );
}

function CampanaWizard({ catalogo, initial, startStep, onClose, onSaved }) {
  const [step, setStep] = useState(startStep || 1);
  const [f, setF] = useState({
    nombre: "", asunto: "", preview_text: "", html: "", from_name: "PD Travel", reply_to: "",
    programada_para: "", motivo_autorizacion: "",
    filtros: { tipos: [], categorias: [], especialidades: [], destinos: [], niveles: [] },
    ...initial,
  });
  const [eligibles, setEligibles] = useState(null);
  const [preview, setPreview] = useState(null); // live preview basado en filtros
  const [previewLoading, setPreviewLoading] = useState(false);
  const [seleccionados, setSeleccionados] = useState(null); // Set<id>. null = "todos" (default)
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [showPreview, setShowPreview] = useState(false);

  const set = (k, v) => setF({ ...f, [k]: v });
  const setFiltro = (k, v) => setF({ ...f, filtros: { ...f.filtros, [k]: v } });

  // Filtro cliente-side por búsqueda dentro del preview
  const matches = (c) => {
    if (!busqueda.trim()) return true;
    const q = busqueda.toLowerCase();
    const nombre = `${c.nombre || ""} ${c.apellidos || ""}`.toLowerCase();
    return nombre.includes(q) || (c.empresa_nombre || "").toLowerCase().includes(q) || (c.correo || "").toLowerCase().includes(q);
  };

  // Live preview: refresca al cambiar filtros
  useEffect(() => {
    let cancel = false;
    const t = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const r = await axios.post(`${API}/directorio/campanas/preview-destinatarios`, { filtros: f.filtros }, { withCredentials: true });
        if (!cancel) {
          setPreview(r.data);
          // Al cambiar los filtros, resetear selección a "todos" los que ahora aplican
          const allIds = [
            ...(r.data.preview || []).map((c) => c.id),
            ...(r.data.cliente_terceros || []).map((c) => c.id),
          ];
          setSeleccionados(new Set(allIds));
        }
      } catch (e) { /* silent */ }
      finally { if (!cancel) setPreviewLoading(false); }
    }, 350);
    return () => { cancel = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(f.filtros)]);

  const toggleContacto = (id) => {
    const s = new Set(seleccionados || []);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSeleccionados(s);
  };
  const setTodos = (marcar) => {
    if (!preview) return;
    if (marcar) {
      const allIds = [
        ...(preview.preview || []).map((c) => c.id),
        ...(preview.cliente_terceros || []).map((c) => c.id),
      ];
      setSeleccionados(new Set(allIds));
    } else {
      setSeleccionados(new Set());
    }
  };

  // Cálculos derivados
  const selectedIds = useMemo(() => Array.from(seleccionados || []), [seleccionados]);
  const tercerosSeleccionados = useMemo(() => {
    if (!preview) return 0;
    const s = seleccionados || new Set();
    return (preview.cliente_terceros || []).filter((c) => s.has(c.id)).length;
  }, [preview, seleccionados]);

  const guardarPaso = async () => {
    if (!f.nombre.trim()) { toast.error("Nombre requerido"); return; }
    if (selectedIds.length === 0) { toast.error("Selecciona al menos un contacto"); return null; }
    if (tercerosSeleccionados > 0 && !f.motivo_autorizacion.trim()) {
      toast.error("Se requiere motivo para incluir Cliente de Terceros seleccionados");
      return null;
    }
    setSaving(true);
    try {
      const payload = { ...f, destinatarios_ids: selectedIds };
      let saved;
      if (f.id) {
        const r = await axios.put(`${API}/directorio/campanas/${f.id}`, payload, { withCredentials: true });
        saved = r.data;
        setF({ ...saved });
      } else {
        const r = await axios.post(`${API}/directorio/campanas`, payload, { withCredentials: true });
        saved = r.data;
        setF({ ...f, ...saved });
      }
      return saved;
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.error === "motivo_autorizacion_requerido") {
        toast.error(d.message);
      } else {
        toast.error((typeof d === "string" ? d : null) || "Error");
      }
      return null;
    }
    finally { setSaving(false); }
  };

  const cargarEligibles = async (id) => {
    try {
      const r = await axios.get(`${API}/directorio/campanas/${id}/eligibles`, { withCredentials: true });
      setEligibles(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const paso1Next = async () => {
    const saved = await guardarPaso();
    if (!saved) return;
    await cargarEligibles(saved.id);
    setStep(2);
  };

  const enviar = async (modo) => {
    setSending(true);
    try {
      const body = { modo };
      if (modo === "schedule" && f.programada_para) body.schedule_at = f.programada_para;
      const r = await axios.post(`${API}/directorio/campanas/${f.id}/enviar`, body, { withCredentials: true });
      if (r.data?.ok) {
        toast.success(`Campaña ${r.data.estatus} — ${r.data.destinatarios} destinatario(s)`);
        onSaved();
      } else {
        toast.error(r.data?.detail || "Error");
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setSending(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="campana-wizard">
        <DialogHeader>
          <div className="flex items-center justify-between gap-3">
            <DialogTitle>Nueva campaña — Paso {step} de 3</DialogTitle>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowPreview(true)}
              className="gap-1.5 mr-6"
              data-testid="camp-btn-preview"
              disabled={!f.html?.trim()}
              title={!f.html?.trim() ? "Escribe el HTML primero (paso 2)" : "Ver cómo se verá el correo"}
            >
              <Eye className="w-3.5 h-3.5" />
              Vista Previa
            </Button>
          </div>
        </DialogHeader>
        {step === 1 && (
          <div className="space-y-3">
            <p className="text-xs text-slate-500">1️⃣ Segmentación: define audiencia con filtros. AND entre filtros, OR dentro.</p>
            <div><Label>Nombre interno *</Label><Input value={f.nombre} onChange={(e) => set("nombre", e.target.value)} data-testid="camp-nombre" /></div>
            <div className="grid grid-cols-2 gap-3">
              <MultiSelect label="Tipo de relación" options={catalogo.tipo_relacion} value={f.filtros.tipos} onChange={(v) => setFiltro("tipos", v)} testid="camp-tipos" />
              <MultiSelect label="Categoría" options={catalogo.categoria} value={f.filtros.categorias} onChange={(v) => setFiltro("categorias", v)} testid="camp-cat" />
              <MultiSelect label="Especialidad" options={catalogo.especialidad} value={f.filtros.especialidades} onChange={(v) => setFiltro("especialidades", v)} testid="camp-esp" />
              <MultiSelect label="Nivel de relación" options={catalogo.nivel_relacion} value={f.filtros.niveles} onChange={(v) => setFiltro("niveles", v)} testid="camp-niv" />
              {(() => {
                const MESES = ["01 · Enero","02 · Febrero","03 · Marzo","04 · Abril","05 · Mayo","06 · Junio","07 · Julio","08 · Agosto","09 · Septiembre","10 · Octubre","11 · Noviembre","12 · Diciembre"];
                const selectedLabels = (f.filtros.meses_cumpleanos || []).map((m) => MESES[m - 1]).filter(Boolean);
                return (
                  <MultiSelect
                    label="Mes de cumpleaños"
                    options={MESES}
                    value={selectedLabels}
                    onChange={(labels) => setFiltro("meses_cumpleanos", labels.map((l) => parseInt(String(l).slice(0, 2), 10)).filter((n) => n >= 1 && n <= 12))}
                    testid="camp-mes-cumples"
                  />
                );
              })()}
              <div className="col-span-2">
                <Label className="text-xs text-slate-600">Ciudad / destino (coma-separadas)</Label>
                <Input value={(f.filtros.destinos || []).join(",")} onChange={(e) => setFiltro("destinos", e.target.value ? e.target.value.split(",").map(s => s.trim()).filter(Boolean) : [])} placeholder="Ej. Punta Cana, CDMX" />
              </div>
              <label className="col-span-2 flex items-center gap-2 text-sm cursor-pointer bg-pink-50 border border-pink-200 rounded-md p-2">
                <input
                  type="checkbox"
                  checked={!!f.es_cumpleanos}
                  onChange={(e) => set("es_cumpleanos", e.target.checked)}
                  data-testid="camp-es-cumpleanos"
                  className="accent-pink-600"
                />
                <span className="text-slate-700">🎂 Campaña automática de cumpleaños (envía cada contacto en su cumple)</span>
              </label>
            </div>

            {/* Preview en vivo */}
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="camp-preview">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
                  <Eye className="w-4 h-4 text-[#0037ff]" />
                  Contactos elegibles con estos filtros
                </p>
                {previewLoading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
              </div>
              {preview && (
                <>
                  <div className="flex gap-3 flex-wrap text-xs items-center">
                    <Badge className="bg-[#0037ff]/10 text-[#0037ff] border-0">Total: {preview.total}</Badge>
                    <Badge className="bg-emerald-100 text-emerald-700 border-0">Elegibles directos: {preview.normales_count}</Badge>
                    {preview.cliente_terceros_count > 0 && (
                      <Badge className="bg-amber-100 text-amber-700 border-0 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> Cliente de Terceros (requieren autorización): {preview.cliente_terceros_count}
                      </Badge>
                    )}
                    <Badge className="bg-slate-800 text-white border-0" data-testid="camp-seleccionados-badge">Seleccionados: {selectedIds.length}</Badge>
                    {preview.total > 0 && (
                      <div className="flex items-center gap-2 ml-auto">
                        <button
                          type="button"
                          onClick={() => setTodos(true)}
                          className="text-xs text-[#0037ff] hover:underline"
                          data-testid="camp-marcar-todos"
                        >Marcar todos</button>
                        <span className="text-slate-300">·</span>
                        <button
                          type="button"
                          onClick={() => setTodos(false)}
                          className="text-xs text-slate-600 hover:underline"
                          data-testid="camp-desmarcar-todos"
                        >Desmarcar todos</button>
                      </div>
                    )}
                  </div>
                  {preview.total > 0 && (
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                      <Input
                        value={busqueda}
                        onChange={(e) => setBusqueda(e.target.value)}
                        placeholder="Buscar entre elegibles por nombre, empresa o correo…"
                        className="pl-7 h-8 text-xs bg-white"
                        data-testid="camp-buscar-contactos"
                      />
                      {busqueda.trim() && (
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-400" data-testid="camp-buscar-matches">
                          {(() => {
                            const n = (preview.preview || []).filter(matches).length + (preview.cliente_terceros || []).filter(matches).length;
                            return `${n} coincidencia${n === 1 ? "" : "s"}`;
                          })()}
                        </span>
                      )}
                    </div>
                  )}
                  {preview.total === 0 && (
                    <p className="text-xs text-slate-400 italic">Ningún contacto coincide con los filtros actuales.</p>
                  )}
                  {preview.preview?.length > 0 && (
                    <div className="max-h-48 overflow-y-auto text-xs bg-white rounded border border-slate-200">
                      {(() => {
                        const rows = preview.preview.filter(matches);
                        if (rows.length === 0 && busqueda.trim()) {
                          return <div className="px-2 py-3 text-slate-400 text-center italic">Sin resultados para "{busqueda}"</div>;
                        }
                        return rows.map((c, i) => (
                          <label
                            key={c.id || i}
                            className="flex items-center gap-2 px-2 py-1 border-b last:border-0 border-slate-100 hover:bg-slate-50 cursor-pointer"
                            data-testid={`camp-row-${c.id}`}
                          >
                            <input
                              type="checkbox"
                              checked={(seleccionados || new Set()).has(c.id)}
                              onChange={() => toggleContacto(c.id)}
                              className="accent-[#0037ff]"
                              data-testid={`camp-check-${c.id}`}
                            />
                            <span className="truncate flex-1">
                              <b>{[c.nombre, c.apellidos].filter(Boolean).join(" ")}</b>
                              <span className="text-slate-500"> · {c.empresa_nombre || "—"}</span>
                            </span>
                            <span className="text-slate-400 truncate ml-2">{c.correo}</span>
                          </label>
                        ));
                      })()}
                    </div>
                  )}
                  {preview.cliente_terceros?.length > 0 && (
                    <div className="rounded-md bg-amber-50 border border-amber-200 p-2 space-y-1.5">
                      <p className="text-xs font-semibold text-amber-800 flex items-center gap-1">
                        <AlertTriangle className="w-3.5 h-3.5" /> Contactos Cliente de Terceros
                      </p>
                      <div className="max-h-32 overflow-y-auto text-xs bg-white rounded border border-amber-200">
                        {preview.cliente_terceros.filter(matches).map((c, i) => (
                          <label
                            key={c.id || i}
                            className="flex items-center gap-2 px-2 py-1 border-b last:border-0 border-amber-100 hover:bg-amber-50 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={(seleccionados || new Set()).has(c.id)}
                              onChange={() => toggleContacto(c.id)}
                              className="accent-amber-600"
                              data-testid={`camp-check-tercero-${c.id}`}
                            />
                            <span className="flex-1 truncate">
                              <b>{[c.nombre, c.apellidos].filter(Boolean).join(" ")}</b>
                              <span className="text-slate-500"> · {c.empresa_nombre || "—"} · {c.correo}</span>
                            </span>
                          </label>
                        ))}
                      </div>
                      {tercerosSeleccionados > 0 && (
                        <div>
                          <Label className="text-xs text-amber-800">Motivo de la autorización (cumplimiento normativo) *</Label>
                          <Textarea
                            rows={2}
                            value={f.motivo_autorizacion}
                            onChange={(e) => set("motivo_autorizacion", e.target.value)}
                            placeholder="Ej. Cliente compartido con productora XYZ para incentivo Q2, se cuenta con el consentimiento verbal."
                            data-testid="camp-motivo"
                            className="bg-white"
                          />
                          <p className="text-[11px] text-amber-700 mt-1">
                            {tercerosSeleccionados} Cliente(s) de Terceros seleccionado(s). Un supervisor o administrador deberá autorizar el envío.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Cancelar</Button>
              <Button onClick={paso1Next} disabled={saving} data-testid="camp-step1-next">{saving ? "..." : "Continuar →"}</Button>
            </DialogFooter>
          </div>
        )}
        {step === 2 && eligibles && (
          <div className="space-y-3">
            <p className="text-xs text-slate-500">2️⃣ Contenido: revisa destinatarios y prepara el mensaje.</p>
            <div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-sm">
              <p><b>{eligibles.total}</b> contactos elegibles serán incluidos (solo activos con correo válido y suscritos en Mailchimp).</p>
            </div>
            <div><Label>Asunto *</Label><Input value={f.asunto} onChange={(e) => set("asunto", e.target.value)} data-testid="camp-asunto" /></div>
            <div><Label>Preview text (opcional)</Label><Input value={f.preview_text} onChange={(e) => set("preview_text", e.target.value)} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>From name *</Label><Input value={f.from_name} onChange={(e) => set("from_name", e.target.value)} data-testid="camp-from-name" /></div>
              <div>
                <Label>Reply-to * <span className="text-[11px] text-slate-400">(email al que responderán)</span></Label>
                <Input type="email" value={f.reply_to} onChange={(e) => set("reply_to", e.target.value)} placeholder="ventas@pdtravelmex.com" data-testid="camp-reply-to" />
              </div>
            </div>
            <div>
              <Label>Contenido HTML *</Label>
              <Textarea rows={10} value={f.html} onChange={(e) => set("html", e.target.value)} placeholder="<h1>Hola *|FNAME|*</h1><p>...</p>" data-testid="camp-html" className="font-mono text-xs" />
              <p className="text-[11px] text-slate-500 mt-1">Merge tags: <code>*|FNAME|*</code>, <code>*|LNAME|*</code>, <code>*|EMPRESA|*</code>, <code>*|PUESTO|*</code></p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setStep(1)}>← Atrás</Button>
              <Button onClick={async () => {
                if (!f.asunto.trim()) { toast.error("El asunto es obligatorio"); return; }
                if (!f.from_name.trim()) { toast.error("El From name es obligatorio"); return; }
                if (!f.reply_to.trim() || !/^[^@]+@[^@]+\.[^@]+$/.test(f.reply_to)) { toast.error("Reply-to requerido y debe ser un email válido"); return; }
                if (!f.html.trim()) { toast.error("El contenido HTML es obligatorio"); return; }
                const saved = await guardarPaso();
                if (saved) setStep(3);
              }} disabled={saving} data-testid="camp-step2-next">{saving ? "..." : "Continuar →"}</Button>
            </DialogFooter>
          </div>
        )}
        {step === 3 && (
          <div className="space-y-3">
            <p className="text-xs text-slate-500">3️⃣ Envío: revisa y programa o envía ahora.</p>
            <div className="rounded-md bg-slate-50 border p-3 text-sm space-y-1">
              <div><b>Campaña:</b> {f.nombre}</div>
              <div><b>Asunto:</b> {f.asunto || <span className="text-red-500">(vacío)</span>}</div>
              <div><b>Destinatarios:</b> {eligibles?.total ?? "—"}</div>
              <div><b>From:</b> {f.from_name} · <b>Reply-to:</b> {f.reply_to || <span className="text-slate-400">(sin reply-to)</span>}</div>
            </div>
            <div>
              <Label>Programar (opcional)</Label>
              <Input type="datetime-local" step="900" value={f.programada_para || ""} onChange={(e) => {
                const v = e.target.value;
                set("programada_para", v ? new Date(v).toISOString() : "");
              }} />
              <p className="text-[11px] text-slate-500 mt-1">Mailchimp solo acepta minutos en 00, 15, 30 o 45. Si eliges otra hora, se redondea al siguiente cuarto automáticamente. Si dejas vacío, se envía inmediatamente al presionar "Enviar ahora".</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setStep(2)}>← Atrás</Button>
              {f.programada_para && (
                <Button onClick={() => enviar("schedule")} disabled={sending} className="bg-blue-600 hover:bg-blue-700" data-testid="camp-schedule">
                  {sending ? "..." : "Programar"}
                </Button>
              )}
              <Button onClick={() => enviar("send")} disabled={sending} className="bg-emerald-600 hover:bg-emerald-700" data-testid="camp-send">
                <Zap className="w-4 h-4 mr-1" /> {sending ? "Enviando..." : "Enviar ahora"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
      {showPreview && (
        <CampanaPreviewModal campana={f} onClose={() => setShowPreview(false)} />
      )}
    </Dialog>
  );
}

function CampanaDetalle({ campana, onClose, onEdit }) {
  const [showPreview, setShowPreview] = useState(false);
  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between gap-3">
            <DialogTitle>{campana.nombre}</DialogTitle>
            {campana.html?.trim() && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowPreview(true)}
                className="gap-1.5 mr-6"
                data-testid="camp-detalle-btn-preview"
              >
                <Eye className="w-3.5 h-3.5" /> Vista Previa
              </Button>
            )}
          </div>
        </DialogHeader>
        <div className="space-y-2 text-sm">
          <div><b>Estatus:</b> {campana.estatus}</div>
          <div><b>Asunto:</b> {campana.asunto || "—"}</div>
          <div><b>From:</b> {campana.from_name} · <b>Reply-to:</b> {campana.reply_to || "—"}</div>
          <div><b>Destinatarios:</b> {campana.resumen_envio?.destinatarios ?? "—"}</div>
          <div><b>Programada para:</b> {campana.programada_para || "—"}</div>
          {campana.mailchimp_campaign_id && <div className="text-xs"><b>MC campaign ID:</b> <code>{campana.mailchimp_campaign_id}</code></div>}
          <div className="border-t pt-2">
            <p className="text-xs font-semibold text-slate-500 mb-1">HTML</p>
            <pre className="text-[10px] bg-slate-50 border rounded p-2 max-h-64 overflow-auto whitespace-pre-wrap">{campana.html || "(vacío)"}</pre>
          </div>
        </div>
        <DialogFooter>
          {onEdit && campana.estatus !== "enviada" && (
            <Button variant="outline" onClick={onEdit} data-testid="camp-detalle-editar" className="border-[#0037ff]/30 text-[#0037ff] hover:bg-[#0037ff]/5">
              <Edit2 className="w-4 h-4 mr-1.5" /> Editar campaña
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
      {showPreview && (
        <CampanaPreviewModal campana={campana} onClose={() => setShowPreview(false)} />
      )}
    </Dialog>
  );
}

// ═══════════════ CAMPAÑA PREVIEW MODAL ═══════════════
const MERGE_TAG_SAMPLES = {
  FNAME: "Juan",
  LNAME: "Pérez",
  NAME: "Juan Pérez",
  EMAIL: "juan.perez@ejemplo.com",
  EMPRESA: "Aeroméxico",
  COMPANY: "Aeroméxico",
  PUESTO: "Director Comercial",
  TITLE: "Director Comercial",
  CIUDAD: "Ciudad de México",
  CITY: "Ciudad de México",
  PAIS: "México",
  COUNTRY: "México",
  TELEFONO: "+52 55 1234 5678",
  PHONE: "+52 55 1234 5678",
  DATE: new Date().toLocaleDateString("es-MX"),
  CURRENT_YEAR: String(new Date().getFullYear()),
};

function renderMergeTags(html) {
  if (!html) return "";
  return html.replace(/\*\|([A-Z0-9_]+)\|\*/g, (match, key) => {
    if (MERGE_TAG_SAMPLES[key] !== undefined) return MERGE_TAG_SAMPLES[key];
    // Fallback: preserve tag visually so the user notices it's unmapped
    return `<span style="background:#fef3c7;color:#92400e;padding:0 4px;border-radius:3px;font-size:0.9em;">[${key}]</span>`;
  });
}

function CampanaPreviewModal({ campana, onClose }) {
  const [device, setDevice] = useState("desktop"); // "desktop" | "mobile"
  const rendered = useMemo(() => renderMergeTags(campana.html || ""), [campana.html]);
  const subject = renderMergeTags(campana.asunto || "").replace(/<[^>]+>/g, "");
  const previewText = renderMergeTags(campana.preview_text || "").replace(/<[^>]+>/g, "");
  const fromName = campana.from_name || "PD Travel";
  const replyTo = campana.reply_to || "";

  const iframeWidth = device === "desktop" ? 600 : 375;

  const iframeSrcDoc = `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><style>body{margin:0;padding:16px;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;color:#0f172a;background:#ffffff;} img{max-width:100%;height:auto;} table{max-width:100%;} a{color:#0037ff;}</style></head><body>${rendered || '<p style="color:#94a3b8;font-style:italic;">(Sin contenido HTML)</p>'}</body></html>`;

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="camp-preview-modal">
        <DialogHeader>
          <div className="flex items-center justify-between gap-3 pr-6">
            <div className="min-w-0">
              <DialogTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-[#0037ff]" />
                Vista previa del correo
              </DialogTitle>
              <p className="text-[11px] text-slate-500 mt-1">
                Los merge tags están reemplazados por datos de ejemplo (ej. <code>*|FNAME|*</code> → Juan).
              </p>
            </div>
            <div className="flex items-center gap-1 bg-slate-100 rounded-md p-0.5 shrink-0">
              <button
                type="button"
                onClick={() => setDevice("desktop")}
                className={`flex items-center gap-1.5 px-2.5 py-1 text-xs rounded transition ${device === "desktop" ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-700"}`}
                data-testid="camp-preview-desktop"
              >
                <Monitor className="w-3.5 h-3.5" /> Escritorio
              </button>
              <button
                type="button"
                onClick={() => setDevice("mobile")}
                className={`flex items-center gap-1.5 px-2.5 py-1 text-xs rounded transition ${device === "mobile" ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-700"}`}
                data-testid="camp-preview-mobile"
              >
                <Smartphone className="w-3.5 h-3.5" /> Móvil
              </button>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto bg-slate-100 -mx-6 px-6 py-4">
          <div
            className="mx-auto bg-white rounded-lg shadow-md border border-slate-200 overflow-hidden transition-all duration-300"
            style={{ width: iframeWidth, maxWidth: "100%" }}
            data-testid="camp-preview-frame-wrapper"
          >
            {/* Header simulando bandeja de entrada */}
            <div className="bg-slate-50 border-b border-slate-200 px-4 py-3 text-xs space-y-1">
              <div className="flex items-baseline gap-2">
                <span className="text-slate-500 w-14 shrink-0">De:</span>
                <span className="font-medium text-slate-800 truncate">
                  {fromName} {replyTo && <span className="text-slate-400 font-normal">&lt;{replyTo}&gt;</span>}
                </span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-slate-500 w-14 shrink-0">Para:</span>
                <span className="text-slate-700 truncate">{MERGE_TAG_SAMPLES.EMAIL}</span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-slate-500 w-14 shrink-0">Asunto:</span>
                <span className="font-semibold text-slate-900 truncate" data-testid="camp-preview-subject">
                  {subject || <span className="text-red-500 font-normal">(asunto vacío)</span>}
                </span>
              </div>
              {previewText && (
                <div className="flex items-baseline gap-2">
                  <span className="text-slate-500 w-14 shrink-0">Preview:</span>
                  <span className="text-slate-500 italic truncate">{previewText}</span>
                </div>
              )}
            </div>
            {/* Iframe con el HTML */}
            <iframe
              title="Vista previa del correo"
              srcDoc={iframeSrcDoc}
              sandbox=""
              className="w-full block bg-white"
              style={{ height: device === "mobile" ? 560 : 520, border: 0 }}
              data-testid="camp-preview-iframe"
            />
          </div>
          <p className="text-center text-[11px] text-slate-400 mt-3">
            {device === "desktop" ? "Vista escritorio · 600px" : "Vista móvil · 375px"}
          </p>
        </div>

        <DialogFooter className="border-t pt-3">
          <Button variant="outline" onClick={onClose} data-testid="camp-preview-close">Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════ GENERAL PANEL ═══════════════
function GeneralPanel({ data, loading, onOpenEmpresa, onDownload }) {
  if (loading && !data) {
    return (
      <div className="flex items-center gap-2 p-8 text-slate-600">
        <Loader2 className="w-5 h-5 animate-spin" /> Cargando vista general…
      </div>
    );
  }
  const resumen = data?.resumen || {};
  const rows = data?.rows || [];

  const StatCard = ({ icon: Icon, label, value, tone }) => (
    <Card className="border-slate-200">
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg border ${tone.border} ${tone.bg} flex items-center justify-center`}>
            <Icon className={`w-5 h-5 ${tone.text}`} strokeWidth={1.75} />
          </div>
          <div>
            <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: "'Mulish', sans-serif" }}>{value ?? 0}</p>
            <p className="text-xs text-slate-500">{label}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-4" data-testid="general-panel">
      {/* Toolbar */}
      <Card>
        <CardContent className="p-4 flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-700 font-medium">Panorama del directorio</p>
            <p className="text-xs text-slate-500">Resumen consolidado de empresas, contactos, interacciones y campañas</p>
          </div>
          <Button
            variant="outline"
            onClick={onDownload}
            data-testid="btn-descargar-general"
            className="border-[#0037ff]/30 text-[#0037ff] hover:bg-[#0037ff]/5"
          >
            <Download className="w-4 h-4 mr-1.5" /> Descargar reporte
          </Button>
        </CardContent>
      </Card>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard icon={Building2} label="Empresas" value={resumen.total_empresas}
          tone={{ border: "border-[#0037ff]/15", bg: "bg-[#0037ff]/5", text: "text-[#0037ff]" }} />
        <StatCard icon={User} label="Contactos" value={resumen.total_contactos}
          tone={{ border: "border-slate-200", bg: "bg-slate-50", text: "text-slate-700" }} />
        <StatCard icon={CheckCircle2} label="Activos" value={resumen.contactos_activos}
          tone={{ border: "border-emerald-200", bg: "bg-emerald-50", text: "text-emerald-600" }} />
        <StatCard icon={AlertTriangle} label="Datos incompletos" value={resumen.contactos_incompletos}
          tone={{ border: "border-red-200", bg: "bg-red-50", text: "text-red-600" }} />
        <StatCard icon={TrendingUp} label="Interacciones (30d)" value={resumen.interacciones_30d}
          tone={{ border: "border-amber-200", bg: "bg-amber-50", text: "text-amber-600" }} />
        <StatCard icon={Send} label="Campañas" value={resumen.campanas_enviadas}
          tone={{ border: "border-[#0a1230]/15", bg: "bg-[#0a1230]/5", text: "text-[#0a1230]" }} />
      </div>

      {/* Tabla combinada */}
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-sm text-slate-700">Empresas con su contacto principal y actividad</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="[&>th]:py-2 [&>th]:text-xs [&>th]:font-semibold">
                <TableHead>Empresa</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Contacto principal</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Teléfono</TableHead>
                <TableHead>Ciudad</TableHead>
                <TableHead className="text-center">Contactos</TableHead>
                <TableHead className="text-center">Interacc.</TableHead>
                <TableHead>Última</TableHead>
                <TableHead>Estatus</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={10} className="text-center text-slate-400 py-8">Sin registros</TableCell></TableRow>
              )}
              {rows.map((r, idx) => (
                <TableRow
                  key={r.id || idx}
                  className="cursor-pointer hover:bg-slate-50 [&>td]:py-1.5"
                  onClick={() => r.id && onOpenEmpresa && onOpenEmpresa(r.id)}
                  data-testid={`general-row-${idx}`}
                >
                  <TableCell className="font-medium text-sm max-w-[200px] truncate" title={r.nombre}>{r.nombre}</TableCell>
                  <TableCell className="text-xs">{r.tipo_relacion || "—"}</TableCell>
                  <TableCell className="text-sm max-w-[180px] truncate">{r.contacto_principal}</TableCell>
                  <TableCell className="text-xs max-w-[180px] truncate">{r.correo_principal}</TableCell>
                  <TableCell className="text-xs">{formatPhone(r.telefono_principal)}</TableCell>
                  <TableCell className="text-xs">{[r.ciudad, r.pais].filter(Boolean).join(", ") || "—"}</TableCell>
                  <TableCell className="text-center text-xs font-medium">{r.contactos_count}</TableCell>
                  <TableCell className="text-center text-xs font-medium">{r.interacciones_count}</TableCell>
                  <TableCell className="text-xs">{r.ultima_interaccion}</TableCell>
                  <TableCell><Badge className={`${estatusColor[r.estatus] || "bg-slate-100 text-slate-600"} text-[10px] px-1.5 py-0`}>{r.estatus || "—"}</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

// ═══════════════ REPORT DIALOG ═══════════════
function ReportDialog({ tipo, filtros, onClose }) {
  const [meta, setMeta] = useState(null);
  const [seleccionados, setSeleccionados] = useState(new Set());
  const [formato, setFormato] = useState("pdf");
  const [descargando, setDescargando] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/directorio/reporte-campos/${tipo}`, { withCredentials: true });
        setMeta(r.data);
        setSeleccionados(new Set(r.data.defaults || []));
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Error");
      }
    })();
  }, [tipo]);

  const toggle = (k) => {
    const s = new Set(seleccionados);
    if (s.has(k)) s.delete(k); else s.add(k);
    setSeleccionados(s);
  };
  const toggleAll = () => {
    if (!meta) return;
    if (seleccionados.size === meta.campos.length) setSeleccionados(new Set());
    else setSeleccionados(new Set(meta.campos.map((c) => c.key)));
  };

  const descargar = async () => {
    if (seleccionados.size === 0) { toast.error("Selecciona al menos un campo"); return; }
    setDescargando(true);
    try {
      const body = { formato, campos: Array.from(seleccionados), filtros };
      const r = await axios.post(`${API}/directorio/reporte/${tipo}`, body, {
        withCredentials: true,
        responseType: "blob",
      });
      // Descargar
      const cd = r.headers["content-disposition"] || "";
      const match = cd.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : `reporte_${tipo}.${formato}`;
      const blob = new Blob([r.data], { type: r.headers["content-type"] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Reporte descargado");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error generando reporte");
    } finally {
      setDescargando(false);
    }
  };

  const titulos = {
    general: "Descargar reporte general",
    empresas: "Descargar reporte de empresas",
    contactos: "Descargar reporte de contactos",
    historial: "Descargar reporte de historial",
    campanas: "Descargar reporte de campañas",
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="report-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="w-5 h-5 text-[#0037ff]" />
            {titulos[tipo]}
          </DialogTitle>
          <p className="text-xs text-slate-500">
            Elige los campos a incluir y el formato. Se aplicarán los filtros activos de la vista.
          </p>
        </DialogHeader>

        {!meta ? (
          <div className="flex items-center gap-2 py-8 text-slate-500 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Cargando campos…
          </div>
        ) : (
          <div className="space-y-4">
            {/* Campos */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs font-semibold uppercase text-slate-600">Campos a incluir</Label>
                <button
                  type="button"
                  onClick={toggleAll}
                  className="text-xs text-[#0037ff] hover:underline"
                  data-testid="report-toggle-all"
                >
                  {seleccionados.size === meta.campos.length ? "Desmarcar todos" : "Seleccionar todos"}
                </button>
              </div>
              <div className="grid grid-cols-2 gap-1.5 rounded-md border border-slate-200 p-3 max-h-64 overflow-y-auto bg-slate-50">
                {meta.campos.map((c) => (
                  <label
                    key={c.key}
                    className="flex items-center gap-2 px-2 py-1 text-sm rounded hover:bg-white cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={seleccionados.has(c.key)}
                      onChange={() => toggle(c.key)}
                      className="rounded accent-[#0037ff]"
                      data-testid={`report-field-${c.key}`}
                    />
                    <span className="text-slate-700">{c.label}</span>
                  </label>
                ))}
              </div>
              <p className="text-[11px] text-slate-500 mt-1">
                {seleccionados.size} campo{seleccionados.size === 1 ? "" : "s"} seleccionado{seleccionados.size === 1 ? "" : "s"}
              </p>
            </div>

            {/* Formato */}
            <div>
              <Label className="text-xs font-semibold uppercase text-slate-600">Formato del archivo</Label>
              <div className="grid grid-cols-2 gap-2 mt-1.5">
                <button
                  type="button"
                  onClick={() => setFormato("pdf")}
                  data-testid="report-formato-pdf"
                  className={`flex items-center justify-center gap-2 px-3 py-3 rounded-lg border-2 transition-all text-sm font-medium ${
                    formato === "pdf"
                      ? "border-[#0037ff] bg-[#0037ff]/5 text-[#0037ff]"
                      : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                  }`}
                >
                  <FileText className="w-4 h-4" />
                  PDF
                </button>
                <button
                  type="button"
                  onClick={() => setFormato("xlsx")}
                  data-testid="report-formato-xlsx"
                  className={`flex items-center justify-center gap-2 px-3 py-3 rounded-lg border-2 transition-all text-sm font-medium ${
                    formato === "xlsx"
                      ? "border-[#0037ff] bg-[#0037ff]/5 text-[#0037ff]"
                      : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                  }`}
                >
                  <FileSpreadsheet className="w-4 h-4" />
                  Excel
                </button>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button
            onClick={descargar}
            disabled={!meta || descargando || seleccionados.size === 0}
            className="bg-[#0037ff] hover:bg-[#002bcc] disabled:opacity-50"
            data-testid="report-descargar"
          >
            {descargando ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Generando…</> : <><Download className="w-4 h-4 mr-1.5" /> Descargar</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════ AUTORIZACIONES PANEL (supervisor/admin) ═══════════════
function AutorizacionesPanel({ active }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set()); // key: `${campana_id}|${contacto_id}`
  const [notas, setNotas] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/directorio/autorizaciones-pendientes`, { withCredentials: true });
      setRows(r.data || []);
      setSelected(new Set());
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (active) load(); }, [active, load]);

  const toggle = (key) => {
    const s = new Set(selected);
    if (s.has(key)) s.delete(key); else s.add(key);
    setSelected(s);
  };
  const toggleAll = () => {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => `${r.campana_id}|${r.contacto_id}`)));
  };

  // Agrupar seleccionados por campaña para poder mandar 1 request por campaña
  const decidir = async (decision) => {
    if (selected.size === 0) { toast.error("Selecciona al menos un contacto"); return; }
    const porCampana = {};
    for (const key of selected) {
      const [cid, ctid] = key.split("|");
      if (!porCampana[cid]) porCampana[cid] = [];
      porCampana[cid].push(ctid);
    }
    setBusy(true);
    try {
      for (const cid of Object.keys(porCampana)) {
        await axios.post(
          `${API}/directorio/campanas/${cid}/autorizaciones/decidir`,
          { contacto_ids: porCampana[cid], decision, notas },
          { withCredentials: true },
        );
      }
      toast.success(decision === "autorizar" ? "Autorizaciones concedidas" : "Autorizaciones denegadas");
      setNotas("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="autorizaciones-panel">
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                Autorizaciones pendientes para campañas
              </p>
              <p className="text-xs text-slate-500">
                Contactos con tipo "Cliente de Terceros" que un usuario incluyó en una campaña y requieren tu aprobación por cumplimiento normativo.
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="autz-reload">
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Recargar
            </Button>
          </div>
          <div>
            <Label className="text-xs text-slate-600">Notas de la decisión (opcional)</Label>
            <Input
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
              placeholder="Ej. Aprobado por Dirección Comercial el 08/09"
              data-testid="autz-notas"
            />
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => decidir("autorizar")}
              disabled={busy || selected.size === 0}
              className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
              data-testid="autz-autorizar"
            >
              <CheckCircle2 className="w-4 h-4 mr-1.5" /> Autorizar seleccionados ({selected.size})
            </Button>
            <Button
              onClick={() => decidir("denegar")}
              disabled={busy || selected.size === 0}
              variant="outline"
              className="border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50"
              data-testid="autz-denegar"
            >
              <Ban className="w-4 h-4 mr-1.5" /> Denegar seleccionados
            </Button>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="[&>th]:py-2 [&>th]:text-xs [&>th]:font-semibold">
                <TableHead className="w-8">
                  <input
                    type="checkbox"
                    checked={rows.length > 0 && selected.size === rows.length}
                    onChange={toggleAll}
                    className="accent-[#0037ff]"
                    data-testid="autz-toggle-all"
                  />
                </TableHead>
                <TableHead>Contacto</TableHead>
                <TableHead>Empresa</TableHead>
                <TableHead>Correo</TableHead>
                <TableHead>Campaña</TableHead>
                <TableHead>Solicitado por</TableHead>
                <TableHead>Motivo</TableHead>
                <TableHead>Solicitado</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-slate-400 py-8">
                    No hay autorizaciones pendientes ✨
                  </TableCell>
                </TableRow>
              )}
              {rows.map((r) => {
                const key = `${r.campana_id}|${r.contacto_id}`;
                return (
                  <TableRow key={key} className="[&>td]:py-1.5">
                    <TableCell>
                      <input
                        type="checkbox"
                        checked={selected.has(key)}
                        onChange={() => toggle(key)}
                        className="accent-[#0037ff]"
                        data-testid={`autz-check-${key}`}
                      />
                    </TableCell>
                    <TableCell className="text-sm font-medium">{r.contacto_nombre}</TableCell>
                    <TableCell className="text-sm">{r.empresa_nombre}</TableCell>
                    <TableCell className="text-xs">{r.contacto_email || "—"}</TableCell>
                    <TableCell className="text-sm max-w-[180px] truncate" title={r.campana_nombre}>{r.campana_nombre}</TableCell>
                    <TableCell className="text-xs">{r.solicitado_por}</TableCell>
                    <TableCell className="text-xs max-w-[240px] truncate" title={r.motivo}>{r.motivo}</TableCell>
                    <TableCell className="text-xs whitespace-nowrap">{(r.solicitado_at || "").slice(0, 10)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}


// ═══════════════ CONFIG PANEL ═══════════════
function ConfigPanel({ canEdit }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ activo: true, api_key: "", server_prefix: "", list_id: "" });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [webhookMsg, setWebhookMsg] = useState("");
  const [syncing, setSyncing] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/directorio/settings/mailchimp`, { withCredentials: true });
      setCfg(r.data);
      setForm({ activo: r.data.activo, api_key: "", server_prefix: r.data.server_prefix, list_id: r.data.list_id });
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form };
      if (!body.api_key || body.api_key.includes("*")) delete body.api_key; // no reenviar enmascarada
      const r = await axios.put(`${API}/directorio/settings/mailchimp`, body, { withCredentials: true });
      setCfg(r.data);
      setForm({ ...form, api_key: "" });
      toast.success("Configuración guardada");
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    try {
      const r = await axios.post(`${API}/directorio/settings/mailchimp/test`, {}, { withCredentials: true });
      if (r.data.ok) toast.success(r.data.detail);
      else toast.error(r.data.detail);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setTesting(false); }
  };

  const [webhookUrl, setWebhookUrl] = useState("");
  const [webhookHint, setWebhookHint] = useState("");

  const registrarWebhook = async () => {
    setWebhookMsg("Registrando…");
    setWebhookHint("");
    try {
      const r = await axios.post(`${API}/directorio/settings/mailchimp/register-webhook`, {}, { withCredentials: true });
      if (r.data.webhook_url) setWebhookUrl(r.data.webhook_url);
      if (r.data.ok) {
        setWebhookMsg(`✅ Webhook registrado correctamente`);
        setWebhookHint("");
      } else {
        setWebhookMsg(`❌ ${r.data.detail}`);
        setWebhookHint(r.data.hint || "");
      }
    } catch (e) { setWebhookMsg(`❌ ${e?.response?.data?.detail || "Error"}`); }
  };

  const copiarUrl = () => {
    if (!webhookUrl) return;
    navigator.clipboard.writeText(webhookUrl);
    toast.success("URL copiada al portapapeles");
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await axios.post(`${API}/directorio/settings/mailchimp/sync-manual`, {}, { withCredentials: true });
      if (r.data.ok) toast.success(`Sincronizados ${r.data.updated} de ${r.data.total_members} miembros`);
      else toast.error(r.data.detail);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setSyncing(false); }
  };

  if (!cfg) return <div className="p-6 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>;

  return (
    <div className="space-y-3 max-w-3xl">
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            Integración con Mailchimp
            {!isAdmin && <Badge variant="outline" className="text-[10px] text-slate-500">Solo administradores</Badge>}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!form.activo} onChange={(e) => setForm({ ...form, activo: e.target.checked })} disabled={!isAdmin} data-testid="mc-activo" />
            <span>Integración activa</span>
          </label>
          <div>
            <Label>API Key</Label>
            <Input placeholder={cfg.api_key_masked || "Pega tu API key de Mailchimp"} value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })} disabled={!isAdmin} data-testid="mc-api-key" />
            <p className="text-[11px] text-slate-500 mt-1">La actual: <code>{cfg.api_key_masked || "(no configurada)"}</code>. Se enmascara al guardar. Obténla en <b>Mailchimp → Account → Extras → API keys</b>.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Server prefix</Label>
              <Input placeholder="us14" value={form.server_prefix} onChange={(e) => setForm({ ...form, server_prefix: e.target.value })} disabled={!isAdmin} data-testid="mc-prefix" />
              <p className="text-[11px] text-slate-500 mt-1">Sufijo de la API key (ej. `abc-<b>us14</b>`)</p>
            </div>
            <div>
              <Label>List / Audience ID</Label>
              <Input placeholder="a1b2c3d4e5" value={form.list_id} onChange={(e) => setForm({ ...form, list_id: e.target.value })} disabled={!isAdmin} data-testid="mc-list-id" />
              <p className="text-[11px] text-slate-500 mt-1"><b>Audience → Settings → Audience name and defaults</b></p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={save} disabled={!isAdmin || saving} data-testid="mc-save">{saving ? "Guardando…" : "Guardar"}</Button>
            <Button variant="outline" onClick={test} disabled={!isAdmin || testing} data-testid="mc-test">{testing ? "Probando…" : "Probar conexión"}</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            Webhook para eventos de Mailchimp
            {!isAdmin && <Badge variant="outline" className="text-[10px] text-slate-500">Solo administradores</Badge>}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <p className="text-sm text-slate-600">Registra el webhook en tu audiencia para que Mailchimp nos avise cuando alguien se desuscriba, cambie de correo o rebote.</p>
          {cfg.webhook_token && <p className="text-xs text-slate-500">Token webhook (interno, no expongas): <code className="bg-slate-100 px-1 py-0.5 rounded">****{cfg.webhook_token.slice(-6)}</code></p>}
          <Button variant="outline" onClick={registrarWebhook} disabled={!isAdmin} data-testid="mc-webhook">
            <RefreshCw className="w-4 h-4 mr-1" /> Registrar webhook
          </Button>
          {webhookMsg && <p className="text-xs text-slate-700 mt-2 whitespace-pre-wrap break-all">{webhookMsg}</p>}
          {webhookHint && (
            <div className="rounded-md bg-amber-50 border border-amber-200 p-3 space-y-2" data-testid="webhook-fallback">
              <p className="text-xs text-amber-900">{webhookHint}</p>
              {webhookUrl && (
                <div className="flex items-center gap-2 bg-white border border-amber-200 rounded p-2 text-xs">
                  <code className="flex-1 break-all text-slate-700" data-testid="webhook-url-copy">{webhookUrl}</code>
                  <Button size="sm" variant="outline" onClick={copiarUrl} className="h-7 text-xs">Copiar</Button>
                </div>
              )}
              <ol className="text-[11px] text-amber-800 list-decimal pl-4 space-y-0.5">
                <li>Entra a Mailchimp → <b>Audience</b> → <b>Manage Audience</b> → <b>Settings</b> → <b>Webhooks</b></li>
                <li>Clic en <b>Create New Webhook</b> y pega la URL de arriba</li>
                <li>Marca los eventos: Subscribes, Unsubscribes, Profile updates, Cleaned addresses, Email address changes</li>
                <li>Guarda. Los eventos empezarán a llegar a este sistema.</li>
              </ol>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Sincronización manual</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <p className="text-sm text-slate-600">Trae el estado (subscribed / unsubscribed / cleaned) de todos los miembros y lo pega a los contactos por correo.</p>
          <p className="text-xs text-slate-500">Última sincronización: <b>{cfg.ultima_sincronizacion ? formatDate(cfg.ultima_sincronizacion) : "nunca"}</b></p>
          <Button variant="outline" onClick={sync} disabled={syncing} data-testid="mc-sync"><RefreshCw className={`w-4 h-4 mr-1 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Sincronizando…" : "Sincronizar ahora"}</Button>
        </CardContent>
      </Card>

      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="p-4">
          <p className="text-xs font-semibold text-amber-800 uppercase mb-1">Requisitos previos en Mailchimp</p>
          <ul className="text-xs text-amber-900 list-disc pl-4 space-y-0.5">
            <li>Dominio <b>pdtravelmex.com</b> autenticado con SPF y DKIM</li>
            <li>Merge fields creados: <code>FNAME</code>, <code>LNAME</code>, <code>EMPRESA</code>, <code>PUESTO</code></li>
            <li>Dirección postal registrada en la audiencia (Mailchimp la exige en el pie de correo)</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
